import { afterActionPaint } from '../shared/after-action-paint';
import { hydrateStatusCreatureDefinitions, projectAbilityStatuses } from '../janus-engine/actions/project-ability-statuses';
import { saveAppliedTruth } from '../shared/applied-truth-transport';
import { memberAppliedFrame, appliedChargeRows, appliedSpawnRows, retireMemberChargePools } from '../janus-engine/transport/member-applied-frame';
import { appliedStatusRows } from '../janus-engine/transport/applied-status-rows';
import { appliedFrameOf, exposeAppliedFrame, stageAppliedEntities, takeAppliedEntities } from './applied-result';
import { awaitPlan as awaitWalkPlan, resumePlan as resumeWalkPlan } from '../janus-engine/actions/deferred-plan.js';
import { observeTruthIntent } from '../janus-engine/telemetry/truth-commit-observer';
import { savedAttackMembers, attackStatusParticipants } from '../janus-engine/actions/attack-members.js';
import { ftCurrentTruthSink } from '../shared/truth-sink';
import { withLogParticipants } from '../shared/log-participant-snapshot';
import { awaitEntityActionReady as awaitEntityCompileReady } from '../shared/entity-compile-ready';
import { withAsyncDeadline } from '../shared/async-deadline';
import { startActionTiming, markActionTiming, bindActionTiming, endActionTiming } from '../shared/action-timing';
import { lazyDeathResources, deathResourcesPending, resumeDeathResources } from '../shared/spawn-browser';
import { resumableRollSource, type RollSource } from '../janus-engine/actions/roll-source';
import { frameCopyOnRead, frameSnapshot, retainHeldBase } from '../janus-engine/ability/frame-snapshot';
import { markStall } from '../shared/stall-mark';
// attack-truth.ts — A2 (one targeted attack): the client-truth ATTACK transport adapter + WS subscriber.
//
// Third schema on the shared per-actor outbox (movement-truth.ts): the pure resolver
// (janus-engine/actions/weapon-attack.ts) rolls and decides, BOTH rows (attacker + target) are mutated in
// place and rendered at once, the commit goes to attack_truth_commit_inc.php (publish-first), and every
// browser applies the frames `provisional` → `committed` / `correction`. A handled attack NEVER reaches
// use_ability_inc.php; a refusal the resolver proves locally is shown without a request.
//
// Two rows, one outbox: the command rides the ATTACKER's outbox (its revision guards the write). The
// target's optimistic scalars are applied alongside but its revision is never stamped optimistically —
// only saved truth (a committed / correction frame, the commit answer) moves it, so a target that is
// also this browser's own walking actor keeps its own optimistic chain intact.
//
// Reconciliation rule (inherited from A1, Codex review 2026-09-06): a rejected or corrected operation
// reloads the encounter on a refused save.
import { weaponAttackRollType, weaponStrayCandidates, recordingRollSource, browserRollSource, WEAPON_ATTACK_SCHEMA, EVADE_ID, OPPORTUNITY_ATTACK_ID, type WeaponAttackReaders, type AdvDisSource } from '../janus-engine/actions/weapon-attack.js';
import { CRIT_CASCADE_RULES } from '../janus-engine/actions/weapon-attack-cascades.js';
import { resolveWeaponAttackWithReactions, attackReactionCommand } from '../janus-engine/actions/weapon-attack-reactions.js';
import { attackVitalityDelta } from '../janus-engine/presentation/attack-roll-rows.js';
import { possessorEnvironmentDefinitionIds } from '../janus-engine/compile/possessor-compile.js';
import { turnDeathReader } from './turn-death-registry.js';
import { legendaryWindowKey, pageLegendaryWindowActions } from '../janus-engine/turns/legendary-window-action.js';
import { abilityTruthReaders, abilityTruthCampaign, applyTurnTransition, tryCommitWeaponObjectAttack, hydrateReferencedDefinitions, hydrateTurnTriggerDefinitions } from './ability-truth.js';
import { FALL_DEFINITIONS } from './fall-truth.js';
import { k4OnHitStepAside } from '../janus-engine/ability/k4-dispatch.js';
import { EXTRA_DAMAGE_ROLL_TAKEN } from '../janus-engine/actions/weapon-attack-extra-damage.js';
import { bindExplicitWeaponAttack, bindNaturalWeaponAttack, weaponAttackSkill, explicitWeaponFamilyRefusal, planWeaponAttackPartner, weaponAttackPartnerSource, plainWeaponAbility, explicitWeaponChargeContext, explicitWeaponSizeAllowed, reportWeaponAttackRefusal, explicitWeaponAttackReaders, explicitWeaponPassiveEntries, type ExplicitWeaponReaders } from '../janus-engine/actions/explicit-weapon-attack.js';
import { readLayerListEntryUuid } from '../janus-engine/compile/inventory-raw-readers.js';
import { abilityEntryId, setRowAbilityCharges } from '../janus-engine/ability/charges/charge-entry.js';
import { entryCharges } from '../janus-engine/actions/ability-preconditions.js';
import { planThrownWeapon, thrownWeaponReceipt, canProjectThrownWeapon, findWeaponAmmo, planWeaponAmmo, weaponInventoryChangeReceipt, type WeaponInventoryChange } from '../janus-engine/actions/weapon-attack-inventory.js';
import { findInventoryRow } from '../janus-engine/inventory/flag-adjust.js';
import { objectWeaponAttackDefinition } from '../janus-engine/ability/object-weapon-attack.js';
import { applyInventoryMoveReceiptToRoots } from '../ft3d-move-handlers/inventory-move-receipt.js';
import { refuseUnported } from '../janus-engine/unported.js';
import { naturalMitigationBaseId } from '../janus-engine/ability/rolls/ignore-natural-damage-mitigation.js';
import { FORTUNATE_ID, fortunateDefinitionSupported, FortunatePending, isFortunateStatus } from '../janus-engine/actions/weapon-attack-fortunate.js';
import { heldInventoryWeaponSelected, hydrateColdWeaponBinding, inventoryWeaponSelected } from '../janus-engine/actions/cold-weapon-bind.js';
import { attackDamageFrame, resolveAttackStatusEffects, syntheticRollSource, PC_DYING_STATUS_IDS, type AbilityUseVerdict } from '../janus-engine/actions/ability-use.js';
import { hasMoveTowardsTarget, planCasterTowardsTarget } from '../janus-engine/ability/movement/move-caster-towards-target.js';
import { shieldedChanges } from '../janus-engine/statuses/shielded.js';
import { attackStatusPlanFailure, mergeAttackStatuses, projectAttackStatuses } from '../janus-engine/actions/attack-status-projection.js';
import { possessorSceneContext } from './possessor-scene.js';
import { applyMaimedProgression, FORTITUDE, MAIMED, maimedStatusLayersSupported, type FortitudeSaveInput } from '../janus-engine/actions/weapon-attack-maimed.js';
import {
  applyAttackTruth as applyStoredAttackTruth, buildWeaponAttackCommand, NATURAL_WEAPON_SKILL,
  type AttackTargetField, type AttackTruthFrame, type WeaponAttackCommand,
} from '../janus-engine/actions/weapon-attack-commit.js';
import {
  MovementTruthRejected, MovementTruthTerminal, applyMovementTruth, applyApsScalar, isMovementTruthAcceptedResult,
  type ActorTruthCommandBase, type MovementTruthAcceptedResult, type MovementTruthRejectedResult, type MovementTruthRow, type MovementTruthTerminalInfo,
} from '../janus-engine/movement/client-truth-commit.js';
import { creatureIsInActiveCombat } from '../roll-init/client-combat-membership.js';
import { checkOpposingTeam } from '../janus-engine/ability/check-opposing-team.js';
import { attackImpactAt, presentAttackAtSend, THROW_FLIGHT_MS } from '../shared/attack-impact-clock.js';
import { catalogShell, encounterField, encounterCreatureField, type CasterArr } from '../shared/array-types.js';
import { casterRowInstalled, handleCasterUpdate } from './caster-update.js';
import { resolvePlainWalkScope, cutRouteAtFootIndex, opportunityLeaveWatchers, opportunityReachExit } from '../janus-engine/movement/plain-walk-scope.js';
import { opportunityWalkZoneWork } from '../janus-engine/movement/opportunity-walk-zones.js';
import { hasSurfaceTypeValue, combinePaoeEntryRecords } from '../janus-engine/ability/paoe-entry-once.js';
import { readPaoeEntryRecords, readSurfaceEntryRecords } from './paoe-entry-records.js';
import { creatureStandsInZone } from './zone-truth.js';
import { resamplePolyline } from '../../../ft3d/engine/ft3d-move-core.js';
import { movementReachDistanceFt } from '../janus-engine/movement/body-geometry.js';
import type { PlainWalkResult, PlainWalkCommitInput } from '../janus-engine/movement/plain-walk.js';
import { fetchAndCacheObject } from './object-cache.js';
import { contingencySpellIds } from '../janus-engine/sanguine/contingency-inputs.js';
import { handleEncounterUpdate } from './encounter-update.js';
import { audioTiers, peekAudioCatalog, pickActionSound, type ActionSoundCue } from '../shared/audio-catalog.js';
import { playActionSound } from './roll-audio-mixer.js';
import { actionVoiceTiers, pageRollSoundContext } from '../shared/roll-sound-route.js';
import { damageMovementStatusFailure } from '../janus-engine/actions/damage-status-scope.js';
import { statusScalar } from '../janus-engine/actions/activity.js';
import { statusCasterId, statusItemId } from '../janus-engine/statuses/expiry';
// Shared attack geometry; movement retains its own OA context below.
import { rulesWithinReach, weaponAttackSpatialContext, weaponAttackFlankingSource } from '../janus-engine/ability/templates/template-weapon-attack-roll.js';
import { casterMayProvide, casterLayerProvider } from '../janus-engine/actions/cast-range-gate.js';
import { pageProviderReaders } from '../janus-engine/ability/caster-spell-layers.js';
import { hydrateCreatureSkillEntry } from '../janus-engine/compile/creature-skill-entry-hydration.js';
import { attackElevationAdv } from '../janus-engine/ability/cover/directional-cover.js';
import { abilityVisibility } from '../janus-engine/ability/visibility.js';

import { HandBack, type JanusCtx } from '../janus-engine/ability/context.js';
import { authoredTriggerKey, collectCarrierCandidates, collectStatusCandidates, gateTriggerCandidate, passesDbGate, passiveActive, TriggerInputUnavailable } from '../janus-engine/reactions/trigger-dispatch.js';
import { pageTriggerReaders } from '../janus-engine/reactions/page-trigger-readers.js';
import { fullDefinition } from '../janus-engine/reactions/php-row-rules.js';
import {
  actorEncounterFor, actorHasStatus, actorOutbox, actorRowFor, bankConfirmedRev, bankLegacyTruth, confirmedApsFor, confirmedRevFor,
  noteFrameSeen, noteOwnOperation, pendingFor, reconcileActorRow, registerTruthSchema, waitForActorMovement, waitForWalkPrefixSave,
  registerMovementReactionResolver, registerMovementReactionPlanner, registerMovementReactionPending, registerMovementStatusHydrator, movementScopeReaders, MovementRefused,
  walkZoneReaders, isWalkRetryAfterLoad, preflightWalkZoneEntries, planWalkZoneEntries, commitPlannedPlainWalk, tryCommitPlainWalk,
  settleWalkZoneWork, dropQueuedZoneExits,
  type ForcedOpportunityMove, type WalkReactionGeometry,
} from './movement-truth.js';
import { reloadRefusedEncounter, refusalOf } from './truth-refusal-notice.js';
import { type TriggerConsent, type TriggerConsentRequest, TRIGGER_CONSENT_REASON, consentKey } from '../janus-engine/ability/trigger-consent.js';
import { CRIT_VARIANT_RULES } from '../janus-engine/authoring/campaign-house-rules';
import { planWithConsent, offerFortunate, triggerController } from './trigger-consent.js';
import { pageSkillEntries } from '../shared/page-skill-entries.js';

type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const scalar = (value: unknown): unknown => { let v = value; while (Array.isArray(v)) v = v[0]; return v; };
const num = (value: unknown, fallback = 0): number => { const n = Number(scalar(value)); return Number.isFinite(n) ? n : fallback; };
const ENDPOINT = '/options/game/actions/attack_truth_commit_inc.php';
const EXPLODING_DICE_RULE = '6AE51489-5732-6F8B-3ACC-45B0B9118F3C';
const DAMAGE_TYPE_BLUDGEONING = '0C528CF8-1B5A-453C-9BDE-73CBBC239A2A';

/** Delivery metadata only; rejected saves reload the encounter. */
interface Pending { encounterId: string; targetFields: AttackTargetField[] }
const pending = new Map<string, Pending>();
registerMovementReactionPending(encounterId => [...pending.values()].some(p => p.encounterId === upper(encounterId) && p.targetFields.includes('movement')));
const seen = new Set<string>();
const fortunatePending = new FortunatePending();
const damageFortunateOffers = new Map<string, string[]>();
const fortunateActorKey = (encounterId: string, actorId: string): string => upper(encounterId) + ':' + upper(actorId);

// ---- readers over the rows the browser holds -----------------------------------------------------
function definitionFor(row: Row | null, encounter: Row | null, id: string, allowLayerlessStatus: boolean | 'any' = false): Row | null {
  const full = (entry: unknown): Row | null => fullDefinition(asRow(entry), allowLayerlessStatus);
  const key = upper(id);
  return full(asRow(row?.catalog)?.[key]) ?? full(asRow(encounter?.catalog)?.[key]) ?? full(window.FateTide?.objectCache?.get(key)) ?? full(window.FateTide?.objectCache?.get(id)) ?? null;
}
/** A missing dependency is named before the draw; it never supplies a default rule. */
class AttackInputUnavailable extends Error { constructor(what: string) { super(what); this.name = 'AttackInputUnavailable'; } }
/** The encounter's OWN campaign (identity checked), or unavailable — never "some campaign", never "no rules". */
function campaignFor(encounter: Row): Row {
  const id = upper(asRow(encounter.encounters)?.encounter_master_parent_id ?? encounter.encounter_master_parent_id);
  const ft = window.FateTide as unknown as { campaignCache?: Map<string, unknown>; currentCampaignArr?: unknown } | undefined;
  const candidates = [asRow(id ? ft?.campaignCache?.get(id) : null), asRow(ft?.currentCampaignArr)];
  const campaignId = (c: Row | null): string => upper(asRow(c?.campaigns)?.campaign_id ?? c?.campaign_id);
  const match = candidates.find(c => c && id && campaignId(c) === id) ?? null;
  // Identity is the requirement. `campaign_house_rules` is lean-ELIDED when the campaign runs none
  // (build_campaign_arr.php:256 adds the key only when non-empty), so an absent key on the matched campaign
  // IS "no rules" — packet attempt 1 (2026-09-08) refused the fixture campaign on exactly that.
  if (!match) throw new AttackInputUnavailable('campaign-unavailable');
  if (match.campaign_house_rules !== undefined && !Array.isArray(match.campaign_house_rules)) throw new AttackInputUnavailable('campaign-house-rules-unreadable');
  return match;
}
/** campaign_arr_has_active_house_rule_type twin: lean rows `{campaign_house_rule_id}`. */
function houseRuleActive(campaign: Row | null, ruleId: string): boolean {
  const rules = campaign?.campaign_house_rules;
  if (!Array.isArray(rules)) return false;
  return rules.some(rule => upper(asRow(rule)?.campaign_house_rule_id ?? rule) === upper(ruleId));
}
function skillIdForVariable(variable: string): string {
  return variable === 'two_handed_melee_weapon_skill' ? '8F94D454-48C2-A031-AED0-943ED5B609AF'
    : variable === 'throwable_weapon_skill' ? '1A3033B2-1FA4-485A-9D0A-1CD0BC92C961'
    : variable === 'one_handed_melee_weapon_skill' ? '7BB6F92D-9C9F-D3D1-80FF-BBDBA3D06017'
    : variable === 'archery_weapon_skill' ? '2A16EE98-E160-89CD-4AC0-529BF36194D2'
    : variable === 'firearms_weapon_skill' ? '850BDFE9-D36A-46AF-AD28-E1C533E140E3' : NATURAL_WEAPON_SKILL;
}
function skillEntry(row: Row, variable: string): Row | null {
  const stored = asRow(asRow(asRow(asRow(row.computed)?.creature)?.skills)?.[variable]);
  return stored ? hydrateCreatureSkillEntry(stored, variable, { byVariable: {}, byId: {} }) : null;
}
/** The trained tier's bonus die. A trained actor whose catalog entry is missing is UNKNOWN, not untrained. */
function tierDieFor(level: number): number {
  const catalog = (window as unknown as { SKILLTIER_CATALOG?: Record<string, Row> }).SKILLTIER_CATALOG;
  const tier = catalog ? Object.values(catalog).find(t => Number(t.skill_tier_level) === level) : undefined;
  const die = Number(tier?.skill_roll_die ?? 0);
  if (!(die > 0)) throw new AttackInputUnavailable('skill-tier-die-unavailable');
  return die;
}
function essenceTotal(row: Row, name: string): number {
  return num(asRow(asRow(asRow(asRow(row.computed)?.creature)?.essences)?.[name])?.total);
}
function distanceFt(a: Row, b: Row): number {
  const p = (row: Row, axis: string): number => num(ftPosCoord(row, axis) ?? asRow(row.position)?.[axis] ?? asRow(row.encounter_creatures)?.[axis]);
  return Math.hypot(p(a, 'x_pos') - p(b, 'x_pos'), p(a, 'y_pos') - p(b, 'y_pos'), p(a, 'z_pos') - p(b, 'z_pos'));
}
/** ONE type reader for every item-type decision. The ENCOUNTER row's own column first: a player character's roster row
 *  carries NO `items` bucket (the PC compile ships `player_characters`), so the items reader answered '' and a PC reduced
 *  to 0 was KILLED instead of entering Dying (A2 S5 packet, 2026-09-08). Blank strings fall through like absent ones. */
function rowItemType(row: Row): string {
  const fromRow = String(scalar(asRow(row.encounter_creatures)?.item_type) ?? '').trim();
  if (fromRow) return fromRow;
  const fromItems = String(ftItemsCol(row, 'item_type') ?? '').trim();
  if (fromItems) return fromItems;
  return asRow(row.player_characters) ? 'player_character' : '';
}

/** Full save input is prepared before the attack's first draw. */
const TRIGGER_CARRIERS = ['passive_ability', 'reaction_ability', 'free_ability', 'menta', 'curse', 'at_will_spell', 'innate_spell_per_long_rest', 'spells_known', 'opportunity_attack_ability'];
// The mover is the OA's target. Its outgoing hit hook cannot fire here; the
// shared save/consequence frame handles roll/status hooks, and the retaliation
// twin below handles opportunity-attack-against after damage.
const INACTIVE_MOVER_OA_TRIGGERS: ReadonlySet<string> = new Set([
  'triggers_after_short_rest', 'triggers_after_long_rest', 'triggers_after_combat_begins',
  'triggers_when_caster_makes_roll_type', 'triggers_on_next_roll_type', 'triggers_on_hit',
  // janus_trigger_helpers.php outcome modifiers and damage_type_rolls.php's outgoing advantage gate.
  'triggers_on_miss', 'triggers_on_critical_hit', 'triggers_on_not_a_critical_hit', 'triggers_if_roll_type_is_at_adv',
  'triggers_when_caster_gains_status_effect', 'triggers_on_opportunity_attack_against',
]);
function hasDefinitionLayers(def: Row | null): boolean { const layers=asRow(def?.item_layer_types); return !!layers && Object.keys(layers).length>0; }
function carrierEntries(row: Row): unknown[] {
  return TRIGGER_CARRIERS.flatMap(key => [...ftLayerEntries(row, key), ...(Array.isArray(row[key]) ? row[key] as unknown[] : [])]);
}
function carrierDefinition(row: Row, encounter: Row | null, entry: unknown): Row | null {
  const e = asRow(entry);
  const nested = e?.item_arr ?? e?.ability_arr ?? e?.status_arr ?? entry;
  const full = asRow(nested);
  if (hasDefinitionLayers(full)) return full;
  const id = upper(typeof nested === 'string' ? nested : e?.ability_id ?? e?.item_id ?? ftItemsCol(full, 'item_id'));
  return id ? definitionFor(row, encounter, id) : null;
}
function carriesSaveTrigger(row: Row, encounter: Row, keys: string[], skipId: string | null = null): boolean {
  for (const entry of carrierEntries(row)) {
    const def = carrierDefinition(row, encounter, entry);
    if (def && skipId && upper(ftItemsCol(def, 'item_id')) === skipId) continue;
    if (!def || keys.some(key => ftLayerEntries(def, key).length > 0)) return true;
  }
  return false;
}
function fortitudeSaveInput(target: Row, attacker: Row, encounter: Row, consumedStatusIds: readonly string[]): FortitudeSaveInput | null {
  const skills = asRow(asRow(asRow(target.computed)?.creature)?.skills);
  if (!skills) return null;
    const entry = asRow(skills.fortitude);
    if (!entry) return null; // defensive-skill fallback differs from elided ordinary tier-zero skills
  const level = num(entry.tier_level);
  if (!Number.isInteger(level) || level < 0 || entry.forced_natural_main_roll !== undefined) return null;
  const unsupported = ['skill_rolls_adv', 'skill_rolls_dis', 'skill_roll_assistance_roll', 'skill_roll_retracting_roll',
    'skill_is_rolled_at_max', 'skill_is_rolled_at_min', 'roll_type_adv', 'roll_type_dis', 'roll_type_against_adv', 'roll_type_against_dis',
    'roll_type_is_rolled_at_max', 'roll_type_is_rolled_at_min', 'roll_type_against_is_rolled_at_max', 'roll_type_against_is_rolled_at_min',
    'contesting_roll_adv_vs_spells', 'contesting_roll_dis_vs_spells', 'contesting_roll_adv_vs_status_effect', 'contesting_roll_dis_vs_status_effect',
    'triggers_when_caster_gains_luck', 'triggers_on_roll_type', 'triggers_on_next_roll_type', 'triggers_on_next_roll_type_against', 'triggers_on_next_roll_type_against_and_hits'];
  if (unsupported.some(key => ftLayerEntries(target, key).length > 0 || num(ftLayerValue(target, key)) !== 0)) return null;
    if (ftLayerEntries(target, 'triggers_when_caster_gains_status_effect').length
      || ftLayerEntries(attacker, 'triggers_when_target_gains_status_effect').length) return null;
    if (carriesSaveTrigger(target, encounter, ['triggers_when_caster_gains_luck', 'triggers_on_unlucky_roll', 'triggers_when_caster_gains_status_effect',
      'triggers_when_caster_makes_roll_type', 'roll_type_adv', 'roll_type_dis', 'roll_type_is_rolled_at_max', 'roll_type_is_rolled_at_min'])
      || carriesSaveTrigger(attacker, encounter, ['triggers_when_target_gains_status_effect'])) return null;
    // skill_roll_arr's is_target walk (lane A8): the save also takes the target's OWN roll_type_against_* sources for the weapon's roll type —
    // the universal Evade is modelled by the resolver (weapon-attack.ts maimedEvadeSource).
    if (carriesSaveTrigger(target, encounter, ['roll_type_against_adv', 'roll_type_against_dis'], EVADE_ID)) return null;
  // Status payloads are not all folded into direct layers. Inspect their definitions as well.
  for (const status of ftCreatureStatuses(target, 'target') as Row[]) {
    // The shared next-roll reader already settled these riders for the attack. Their single connections end before this save.
    if (consumedStatusIds.includes(upper(scalar(status.status_id)))) continue;
    const id = upper(scalar(status.status_item_id));
    if (id === MAIMED) return null; // pre-damage overflow is not a post-damage status tail
      const embedded = asRow(status.status_arr);
      const def = hasDefinitionLayers(embedded) ? embedded : definitionFor(target, encounter, id);
    if (!def) return null;
    const layers = asRow(def.item_layer_types);
    if (!layers) return null;
      if (!maimedStatusLayersSupported(layers)) return null;
  }
  const stacks = num(entry.adv_dis_stacks);
  const trace: FortitudeSaveInput['trace'] = [];
  if (stacks !== 0) {
    const lines = asRow(entry.adv_dis_breakdown)?.lines;
    if (Array.isArray(lines) && lines.length) {
      for (const raw of lines) {
        const line = asRow(raw); if (!line) return null;
        const delta = num(line.value); if (!delta) continue;
        if (line.global_layer || String(line.label ?? '').startsWith('Global — ')) return null;
          const id = String(line.href_id ?? '');
          // Compiled status provenance links the STATUS INSTANCE, not its catalog definition.
          // Keep that href in the log, but resolve its name/type through status_item_id.
          const status = (ftCreatureStatuses(target, 'target') as Row[]).find(s => upper(scalar(s.status_id)) === upper(id));
          const statusEmbedded = asRow(status?.status_arr);
          const statusDef = status ? (hasDefinitionLayers(statusEmbedded) ? statusEmbedded : definitionFor(target, encounter, upper(scalar(status.status_item_id)))) : null;
          const source = statusDef ?? asRow(catalogShell(target, id)) ?? asRow(catalogShell(encounter, id)) ?? definitionFor(target, encounter, id);
        const label = String(line.label ?? ftItemsCol(source, 'item_name') ?? '');
        const kind = String(line.kind ?? ftItemsCol(source, 'item_type') ?? '');
        if (!label || !kind) return null;
        trace.push({ delta, source: 'skill_base', label, ...(id ? { item_id: id } : {}), kind, origin_name: 'Fortitude', origin_item_id: FORTITUDE });
      }
      if (trace.reduce((sum, e) => sum + e.delta, 0) !== stacks) return null;
    } else trace.push({ delta: stacks, source: 'skill_base', label: 'Skill Base', kind: 'skill_training', origin_name: 'Fortitude', origin_item_id: FORTITUDE });
  }
    if (!Number.isInteger(stacks) || Math.abs(stacks) > 18) return null;
    if (trace.length + Number(level === 0) + Number(num(asRow(target.encounter_creatures)?.luck) === -1) + 1 > 16) return null;
  const pc = asRow(target.player_characters);
  const pcId = upper(ftItemsCol(pc, 'item_id'));
  const ascension = rowItemType(target) === 'player_character' && level > 0;
  return { tierDie: level > 0 ? tierDieFor(level) : null, untrained: level === 0, essence: essenceTotal(target, 'body'),
    skillMod: num(entry.skill_roll_mod), globalMod: num(entry.skill_rolls_mod), stacks, trace, fortune: essenceTotal(target, 'fortune'),
    ascensionThreshold: ascension ? 20 + num(ftLayerValue(target, 'ascension_skill_point_range_mod')) : null,
    ...(ascension && pc && pcId ? { ascensionState: { itemId: pcId, before: num(ftItemsCol(pc, 'sp_as_fortitude')),
      total: ['sp_a_', 'sp_t_', 'sp_as_'].reduce((sum, prefix) => sum + num(ftItemsCol(pc, prefix + 'fortitude')), 0), tierLevel: level },
      tierDieForLevel: tierDieFor } : {}),
  };
}

function fortunateOnGain(row: Row, encounter: Row, gained = false): boolean {
  const statuses = ftCreatureStatuses(row, 'target') as Row[];
  const ec = asRow(row.encounter_creatures);
  const luckAfterSpend = num(ec?.luck) === -1 ? 0 : num(ec?.luck);
  // Natural-one and competing luck-gain carriers still need their own roll executor. Status-gain listeners are served by the shared
  // applicator: a player's accepted offer is a normal cast; an automatic NPC grant travels in this attack's native status verdict.
  const naturalOneKeys = ['triggers_on_unlucky_roll'];
  const luckKeys = ['triggers_when_caster_gains_luck'];
  const refuseCarrying = (keys: string[]): void => {
    if (keys.some(k => ftLayerEntries(row, k).length > 0)) throw new AttackInputUnavailable('fortunate-trigger-combination-out-of-scope');
    for (const entry of carrierEntries(row)) {
      const d = carrierDefinition(row, encounter, entry);
      if (!d || keys.some(k => ftLayerEntries(d, k).length > 0)) throw new AttackInputUnavailable('fortunate-carrier-out-of-scope');
    }
    for (const status of statuses) {
      const embedded = asRow(status.status_arr);
      const d = hasDefinitionLayers(embedded) ? embedded : definitionFor(row, encounter, upper(scalar(status.status_item_id)), true);
      // The shared swing consumes this next-roll status before any luck gain; its own universal trigger is supported.
      if (isFortunateStatus(status) && fortunateDefinitionSupported(asRow(d?.item_layer_types) ?? {})) continue;
      if (!d || keys.some(k => ftLayerEntries(d, k).length > 0)) throw new AttackInputUnavailable('fortunate-status-trigger-out-of-scope');
    }
  };
  refuseCarrying(naturalOneKeys);
  if (gained ? num(ftEncounterCreatureField(row.encounter_creatures, 'luck')) <= 0
    : essenceTotal(row, 'fortune') <= luckAfterSpend) return false;
  refuseCarrying(luckKeys);
  if (num(ec?.reaction) <= 0) return false;
  if (knownPlayerRow(encounter, row)) return false; // its committed gain offers the ordinary Fortunate cast
  const def = definitionFor(row, encounter, FORTUNATE_ID);
  if (!def || !fortunateDefinitionSupported(asRow(def.item_layer_types) ?? {})) throw new AttackInputUnavailable('fortunate-definition-unavailable');
  // A self Luck event has no inciting target id, just like the shared trigger dispatcher.
  if (row.triggers !== undefined && !Array.isArray(row.triggers)) throw new AttackInputUnavailable('fortunate-trigger-settings-unavailable');
  return passesDbGate({ value: ftLayerValue }, FORTUNATE_ID, row, null);
}

/** A creature whose controller the page KNOWS is a player (websocket/trigger-consent.ts triggerController — a user id, never '?' / null). */
function knownPlayerRow(encounter: Row, row: Row): boolean {
  const encounterId = upper(asRow(encounter.encounters)?.encounter_id ?? encounter.encounter_id ?? asRow(row.encounter_creatures)?.encounter_id);
  const who = encounterId ? triggerController(encounterId, row) : null;
  return !!who && who !== '?';
}
function explicitWeaponReaders(attacker: Row, encounter: Row): ExplicitWeaponReaders {
  return { value: ftLayerValue, entries: ftLayerEntries, inventoryField: ftInventoryField,
    creatureField: (row, key) => ftEncounterCreatureField(row.encounter_creatures, key),
    itemField: ftItemsCol, computedValue: ftComputedValue, itemType: row => String(ftItemsCol(row, 'item_type') ?? ''),
    resolveItem: id => definitionFor(attacker, encounter, id, true),
    skillEntries: pageSkillEntries() };
}
function readers(attacker: Row, encounter: Row, bound?: { weapon: Row; skill: string; inventoryId: string }): WeaponAttackReaders {
  const campaign = campaignFor(encounter);
  const skillFor = (weapon: Row): string => bound && weapon === bound.weapon ? bound.skill : weaponAttackSkill(weapon, attacker, { value: ftLayerValue, entries: ftLayerEntries });
  const weaponRead = bound ? explicitWeaponAttackReaders(bound.weapon, attacker, explicitWeaponReaders(attacker, encounter)) : null;
  return {
    attackSpatial: (caster, target, weapon, options) => weaponAttackSpatialContext(opportunityFlankContext(encounter), caster, target,
      { ...options, weapon, visibility: abilityVisibility(encounter, caster, target) }),
    turnDeath: (enc, members, dyingId) => turnDeathReader()?.(enc, members, dyingId)
      ?? { kind: 'handback', reason: 'turn-death:reader-not-held' },
    // OWNER RULING 2026-10-03 (Fortunate): a KNOWN player's natural-20 luck gain fires no Fortunate inside the swing — the page offers it
    // after the commit (accepted → offerFortunate). An NPC / GM creature (and a controller the page cannot name) keeps the auto-fire.
    fortunateOnGain: (row, gained) => fortunateOnGain(row, encounter, gained),
    value: (row, key) => weaponRead ? weaponRead.value(row, key) : ftLayerValue(row, key),
    creatureField: (row, key) => ftEncounterCreatureField(row.encounter_creatures, key),
    encounterField: ftEncounterField,
    entries: (row, key) => weaponRead ? weaponRead.entries(row, key) : ftLayerEntries(row, key),
    definition: id => bound && upper(id) === upper(bound.inventoryId) ? bound.weapon : definitionFor(attacker, encounter, id),
    // S10: the TARGET's extra-damage-taken passives and status definitions live in the target row's own catalog.
    definitionOn: (row, id) => definitionFor(row, encounter, id, true),
    statusRows: row => ftCreatureStatuses(row, 'target') as Row[],
    conditionScene: row => possessorSceneContext(encounter, row).conditionScene?.() ?? null,
    inCombat: (enc, row) => creatureIsInActiveCombat(enc, row as never),
    opposingTeams: (left, right) => checkOpposingTeam(opportunityFlankContext(encounter), left, right),
    armorClass: row => { const total = asRow(asRow(asRow(asRow(asRow(row.computed)?.creature)?.ac)?.ac))?.total; return total === undefined || total === null ? null : num(total, NaN); },
    skillTierDie: (row, weapon) => { const level = num(skillEntry(row, skillFor(weapon))?.tier_level); return level > 0 ? tierDieFor(level) : null; },
    // The compiled entry's tier (tier 0 = untrained → the `untrained` disadvantage). The browser row ELIDES every tier-0
    // entry (probe 2026-09-08: the untrained Wizard's `computed.creature.skills` lists only its trained skills, while PHP's
    // full table marks natural_weapon_skill `is_flawed`), so an ABSENT entry under a present skills cluster IS tier 0; only
    // a missing cluster is unknown (null → hands back).
    skillTierLevel: (row, weapon) => { const skills = asRow(asRow(asRow(row.computed)?.creature)?.skills); if (!skills) return null; const entry = asRow(skills[skillFor(weapon)]); return entry ? num(entry.tier_level) : 0; },
    skillAdvDisStacks: (row, weapon) => num(skillEntry(row, skillFor(weapon))?.adv_dis_stacks),
    skillAdvDisSources: (row, weapon) => {
      const entry = skillEntry(row, skillFor(weapon));
      const lines = asRow(entry?.adv_dis_breakdown)?.lines;
      const sources: AdvDisSource[] = [];
      for (const raw of Array.isArray(lines) ? lines : []) {
        const line = asRow(raw), layer = line?.global_layer;
        if (layer !== 'skill_rolls_adv' && layer !== 'skill_rolls_dis') continue;
        const stacks = num(line?.value);
        if (stacks) sources.push({ kind: layer, label: `Skill Rolls ${layer === 'skill_rolls_adv' ? 'ADV' : 'DIS'} (${line?.label ?? 'Global'})`, stacks });
      }
      const base = num(entry?.adv_dis_stacks) - sources.reduce((sum, source) => sum + source.stacks, 0);
      if (base) sources.unshift({ kind: 'skill_base', label: 'Skill', stacks: base });
      return sources;
    },
    skillRollMod: (row, weapon) => num(skillEntry(row, skillFor(weapon))?.mod),
    // The ACTIVE status catalog ids on a row (the `statuses.target` rows; the def ref rides under status_item_id, or the
    // resolved def's items.item_id) — for Evade's if_caster_has_status_effect_not_active gate.
    statusItemIds: row => (ftCreatureStatuses(row, 'target') as Row[]).map(s => String(scalar(s.status_item_id) || scalar(asRow(asRow(s.status_arr)?.items)?.item_id) || '')).filter(id => id !== ''),
    extraAttack: row => num(asRow(asRow(asRow(row.computed)?.creature)?.combat)?.extra_attack),
    essenceBonus: row => essenceTotal(row, 'body'),
    damageEssenceBonus: (row, weapon) => {
      if (upper(ftLayerValue(weapon, 'roll_type')) !== '3F85CA97-F9B2-A83A-FDE8-E523FD99346C' || ftLayerEntries(weapon, 'item_throwable').length) return essenceTotal(row, 'body');
      const provider = casterLayerProvider(pageProviderReaders({ value: ftLayerValue, entries: ftLayerEntries,
        statuses: r => ftCreatureStatuses(r, 'target'), definition: id => definitionFor(row, encounter, id) }), row, 'precision_shots');
      if (provider === undefined) throw new Error('weapon:precision-shots-provider-unavailable');
      return provider ? essenceTotal(row, 'body') : 0;
    },
    // REACH = the front-end rule (owner 2026-09-28, lane RX): ft3d-combat-distance-v3 with both bodies' footprints — the caster's ring
    // only has to touch the target's size-tier footprint, as the scene's approach planner measures it.
    // Mesh bounds describe movement clearance, not reach. An unknown size remains unmeasurable.
    distanceFt: (a, b) => {
      const move = movementScopeReaders(encounter), ga = move.geometry(a as never), gb = move.geometry(b as never);
      if (!ga || !gb) return NaN;
      const pa = move.position(a as never), pb = move.position(b as never);
      return movementReachDistanceFt(ga, { x: pa[0], y: pa[1], z: pa[2] }, gb, { x: pb[0], y: pb[1], z: pb[2] });
    },
    reachFt: row => num(asRow(asRow(asRow(row.computed)?.creature)?.reach)?.total, NaN),
    critVariant: () => CRIT_VARIANT_RULES.find(([, id]) => houseRuleActive(campaign, id))?.[0] ?? 'maximum',
    explodingDiceActive: () => houseRuleActive(campaign, EXPLODING_DICE_RULE),
    critCascadeRules: () => ({ remarkable: houseRuleActive(campaign, CRIT_CASCADE_RULES.remarkable.id),
      worldShaking: houseRuleActive(campaign, CRIT_CASCADE_RULES.worldShaking.id), maimed: houseRuleActive(campaign, CRIT_CASCADE_RULES.maimed.id) }),
    fortitudeSave: (target, _weapon, consumedStatusIds) => fortitudeSaveInput(target, attacker, encounter, consumedStatusIds),
    fortune: row => essenceTotal(row, 'fortune') || num(ftLayerValue(row, 'fortune')),
    // damage_type_roll.php defaults a weapon without a damage_type layer to Bludgeoning (the derived Slam has none).
    // RAW (trimmed, never re-cased): the resolver matches the target's defence entries with PHP's strict compare (S6, Codex F1).
    damageTypeId: weapon => String(scalar(ftLayerValue(weapon, 'damage_type')) ?? '') || DAMAGE_TYPE_BLUDGEONING,
    // The ENCOUNTER row's own column first: a player character's roster row carries NO `items` bucket at all (the PC
    // compile ships `player_characters` instead), so the items reader answered '' and the resolver classified a PC
    // reduced to 0 as KILLED instead of DYING (A2 S5 packet, 2026-09-08 — a lethal divergence, caught on the dummy).
    itemType: rowItemType,
    vitality: (row, field) => { const v = asRow(asRow(row.computed)?.vitality)?.[field]; return v === undefined || v === null ? null : num(v, NaN); },
    // Legacy kills only an UNCONTROLLED creature (is_creature_not_a_summon): a plain summon destroys, a hunter summon
    // hides, an animated summon kills — every controlled creature hands back here (fail closed; Codex S5 review F3).
    isSummon: row => { const ec = asRow(row.encounter_creatures) ?? {}; return rowItemType(row) === 'creature' && !!scalar(ec.controller_encounter_creature_id); },
    isOnTurn: (enc, row) => upper(encounterField(enc, 'current_encounter_creature_id_turn')) === upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id')),
    hasStatus: (row, name) => actorHasStatus(row, name),
    uuid: () => crypto.randomUUID().toUpperCase(),
  };
}


/** One held partner view for commit and dry-run planning; no cross-bundle state. */
function weaponPartnerReaders(read: WeaponAttackReaders, attacker: Row, encounter: Row,
  projectiles = new Map<string, { finish: (hit: boolean, roll: import('../janus-engine/actions/roll-source.js').RollSource) => WeaponInventoryChange }>(), paidProjectile = false): WeaponAttackReaders {
  const bindingRead = explicitWeaponReaders(attacker, encounter);
  const partners = new Map<string, Row>(), views = new WeakMap<Row, ExplicitWeaponReaders>();
  return { ...read,
    definition: id => partners.get(upper(id)) ?? read.definition(id),
    weaponEntries: (weapon, row, key) => {
      let view = views.get(weapon);
      if (!view) { view = explicitWeaponAttackReaders(weapon, attacker, bindingRead); views.set(weapon, view); }
      return view.entries(row, key);
    },
    partnerWeapon: (paid, caster, victim, hand) => {
      const partner = planWeaponAttackPartner(paid, caster, victim, bindingRead, read, hand);
      if (partner && !('reason' in partner)) {
        partners.set(upper(partner.weaponId), partner.weapon);
        if (bindingRead.entries(partner.weapon, 'uses_ammo_classification').length) {
          // The current save carries one projectile change. Two shots need a multi-projectile contract.
          if (paidProjectile || bindingRead.entries(paid, 'uses_ammo_classification').length) return { reason: 'rider-multiple-projectiles-out-of-scope' };
          const ammo = findWeaponAmmo(caster, partner.weapon, bindingRead);
          if (!ammo) return null;
          const targetId = upper(ftEncounterCreatureField(victim.encounter_creatures, 'encounter_creature_id'));
          const encounterId = upper(ftEncounterField(encounter, 'encounter_id'));
          const plan = planWeaponAmmo(ammo, victim, encounter, encounterId, targetId, bindingRead, ftPosCoord,
            () => crypto.randomUUID().toUpperCase(), row => (ftCreatureStatuses(row, 'target') as Row[]).map(st => String(scalar(st.status_item_id) ?? '')));
          if ('reason' in plan) return { reason: 'rider-' + plan.reason };
          partner.weapon = plan.weapon(partner.weapon);
          partners.set(upper(partner.weaponId), partner.weapon);
          projectiles.set(targetId, plan);
        }
      }
      return partner;
    } };
}

// ---- outbox handlers ------------------------------------------------------------------------------
const commandOf = (command: ActorTruthCommandBase): WeaponAttackCommand => command as unknown as WeaponAttackCommand;
/** Old in-flight carriers cannot be projected partially. Read fresh native truth. */
function rejectRetiredAttack(command: ActorTruthCommandBase): boolean {
  const wire = command as unknown as Row;
  const frame = asRow(wire.frame), authoritative = asRow(wire.authoritative);
  if (![wire, asRow(frame?.target), asRow(authoritative?.target)].some(row => row
    && ['damage_reaction', 'damage_reaction_state'].some(key => Object.prototype.hasOwnProperty.call(row, key)))) return false;
  refuseUnported('attack:retired-damage-reaction');
  recoverStatusProjection(command.encounter_id, 'attack:retired-damage-reaction');
  return true;
}
const TARGET_FIELDS = ['v_current', 'v_temp', 'reaction', 'encounter_creature_dead', 'luck', 'movement'] as const;
/** The target fields an operation WROTE (its frame's target keys) — the only ones its revert may touch. */
const writtenTargetFields = (frame: AttackTruthFrame | null | undefined): AttackTargetField[] =>
  TARGET_FIELDS.filter(field => frame?.target !== undefined && (frame.target as Row)[field] !== undefined);
function render(command: ActorTruthCommandBase, attacker: MovementTruthRow, target: MovementTruthRow | null): void {
  const stray = commandOf(command).frame.stray;
  if (stray) {
    const row = actorRowFor(command.encounter_id, stray.target_id);
    if (row) handleCasterUpdate({ type: 'caster_update', caster_arr: row as unknown as CasterArr, local: true });
  }
  handleCasterUpdate({ type: 'caster_update', caster_arr: attacker as unknown as CasterArr, local: true });
  if (target) handleCasterUpdate({ type: 'caster_update', caster_arr: target as unknown as CasterArr, local: true });
  window.dispatchEvent(new CustomEvent('ft:attack-truth-applied', { detail: { command, get applied_frame() { return appliedFrameOf(command); } } }));
  const movement = commandOf(command).movement;
  if (target && movement) window.dispatchEvent(new CustomEvent('ft:movement-truth-applied', { detail: { command: {
    schema: 'janus.movement.truth.v1', operation_id: command.operation_id, encounter_id: command.encounter_id,
    actor_id: commandOf(command).target_id, expected_rev: commandOf(command).target_expected_rev,
      before: movement.before, updates: { x_pos: Number(ftPosCoord(target, 'x_pos') ?? 0), y_pos: Number(ftPosCoord(target, 'y_pos') ?? 0),
        z_pos: Number(ftPosCoord(target, 'z_pos') ?? 0), movement_pct: Number(ftEncounterCreatureField(target.encounter_creatures, 'movement_pct') ?? 0) },
      route: movement.route,
  } } }));
}
function targetRow(command: ActorTruthCommandBase): MovementTruthRow | null {
  return actorRowFor(command.encounter_id, commandOf(command).target_id);
}
const projectedThrows = new Set<string>();
/** Inventory effects retain the thrown identity on local and peer commands. Ammo consumes a different row.
 * A native ranged throwing weapon also uses its captured throwable skill without an explicit Throw toggle. */
function presentWeaponAttack(command: WeaponAttackCommand): number {
  const actor = actorRowFor(command.encounter_id, command.actor_id);
  const weapon = actor ? findInventoryRow(actor, command.weapon_id) : null;
  const rangedThrowable = weapon && upper(ftLayerValue(weapon, 'roll_type')) === '3F85CA97-F9B2-A83A-FDE8-E523FD99346C'
    && ftLayerEntries(weapon, 'item_throwable').length > 0;
  const thrown = !!command.inventory_move || command.inventory_change?.inventory_id === command.weapon_id
    || command.rolls?.attack?.roll_type_2 === '1A3033B2-1FA4-485A-9D0A-1CD0BC92C961' || !!rangedThrowable;
  const now = Date.now(), impactAt = attackImpactAt(command.operation_id, command.actor_id, now, thrown ? THROW_FLIGHT_MS : 0);
  presentAttackAtSend(command.operation_id, command.actor_id, command.target_id, command.frame.outcome.hit === true, impactAt, now,
    thrown ? { inventoryId: command.weapon_id, arcing: false } : undefined);
  return impactAt;
}
function projectInventorySnapshot(command: ActorTruthCommandBase, state: unknown, rev?: number): void {
  if (!Array.isArray(state)) return;
  const encounter = actorEncounterFor(command.encounter_id), actor = actorRowFor(command.encounter_id, command.actor_id);
  if (!encounter || !actor) return;
  if (Number.isInteger(rev) && confirmedRevFor(command.encounter_id, command.actor_id, actor) > rev!) return;
  const roots = [actor, encounter, ...(Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : [])] as Row[];
  for (const raw of state) {
    const entry = asRow(raw), id = upper(entry?.inventory_id), saved = asRow(entry?.row);
    if (!id) continue;
    const existing = roots.map(root => findInventoryRow(root, id)).find(Boolean);
    const qty = saved ? Number(ftInventoryField(saved, 'item_qty') ?? 0) : 0;
    const holder = saved ? String(ftInventoryField(saved, 'possess_id') ?? '') : String(ftInventoryField(existing, 'possess_id') ?? '');
    const receipt = {
      schema: 'ft3d.inventory-move-receipt.v1' as const, ok: true as const, mutation_id: command.operation_id + ':' + id,
      move: { outcome: 'full_move' as const, moved_qty: 0,
        source: { inventory_id: existing ? id : command.operation_id, possess_id_before: String(ftInventoryField(existing, 'possess_id') ?? holder), item_qty_after: 0, deleted_after: true },
        destination: { inventory_id: id, possess_id: holder, item_qty_after: qty, created: !existing,
          deleted_after: !saved || Number(ftInventoryField(saved, 'inventory_deleted') ?? 0) !== 0,
          equipped: saved ? String(ftInventoryField(saved, 'equipped') ?? '') : '',
          position: { x: Number(ftPosCoord(saved, 'x_pos') ?? 0), y: Number(ftPosCoord(saved, 'y_pos') ?? 0), z: Number(ftPosCoord(saved, 'z_pos') ?? 0) },
          ...(saved ? { row: { ...existing, ...saved } } : {}) }, roots: { source: String(ftInventoryField(existing, 'possess_id') ?? holder), destination: holder } },
      affected_caster_ids: [command.actor_id], affected_encounter_ids: [command.encounter_id], timing: { accepted_unix_ms: 0, commit_ms: 0 },
    };
    applyInventoryMoveReceiptToRoots(receipt, roots);
    window.dispatchEvent(new CustomEvent('ft:inventory-move-receipt', { detail: { receipt } }));
  }
}
function projectStrayTruth(command: ActorTruthCommandBase, state: AttackTruthFrame['stray'], saved = false): void {
  if (!state) return;
  const row = actorRowFor(command.encounter_id, state.target_id);
  if (!row || confirmedRevFor(command.encounter_id, state.target_id, row) > state.rev) return;
  if (state.reaction === 0) delete row.encounter_creatures.reaction; else row.encounter_creatures.reaction = state.reaction;
  if (saved) {
    bankConfirmedRev(command.encounter_id, state.target_id, row, state.rev, {});
    if (pendingFor(command.encounter_id, state.target_id) === 0) row.encounter_creatures.movement_rev = state.rev;
  }
}
function projectThrownWeapon(command: ActorTruthCommandBase): void {
  const ammo = commandOf(command).inventory_change;
  if (ammo && !projectedThrows.has(command.operation_id)) {
    const actor = actorRowFor(command.encounter_id, command.actor_id), encounter = actorEncounterFor(command.encounter_id);
    if (!actor || !encounter || confirmedRevFor(command.encounter_id, command.actor_id, actor) > command.expected_rev + 1) return;
    const held = findInventoryRow(actor, ammo.inventory_id);
    if (!held || String(ftInventoryField(held, 'possess_id') ?? '') !== ammo.before.possess_id
        || Number(ftInventoryField(held, 'item_qty') ?? 0) !== ammo.before.item_qty) return;
    const receipt = weaponInventoryChangeReceipt(ammo, command.actor_id, command.encounter_id, command.operation_id);
    applyInventoryMoveReceiptToRoots(receipt, [actor, encounter]);
    projectedThrows.add(command.operation_id);
    if (projectedThrows.size > 256) projectedThrows.delete(projectedThrows.values().next().value!);
    window.dispatchEvent(new CustomEvent('ft:inventory-move-receipt', { detail: { receipt } }));
  }
  const move = commandOf(command).inventory_move;
  if (!move || projectedThrows.has(command.operation_id)) return;
  const actor = actorRowFor(command.encounter_id, command.actor_id);
  const encounter = actorEncounterFor(command.encounter_id);
  if (!actor || !encounter) return;
  if (confirmedRevFor(command.encounter_id, command.actor_id, actor) > command.expected_rev + 1) return;
  if (!canProjectThrownWeapon(move, actor as Row, { value: ftLayerValue, entries: ftLayerEntries, inventoryField: ftInventoryField,
    itemField: ftItemsCol, computedValue: ftComputedValue, itemType: row => String(ftItemsCol(row, 'item_type') ?? '') })) return;
  applyInventoryMoveReceiptToRoots(thrownWeaponReceipt(move, command.operation_id), [actor, encounter]);
  projectedThrows.add(command.operation_id);
  if (projectedThrows.size > 256) projectedThrows.delete(projectedThrows.values().next().value!);
  window.dispatchEvent(new CustomEvent('ft:inventory-move-receipt', { detail: { receipt: thrownWeaponReceipt(move, command.operation_id) } }));
}
function remember(operationId: string, command: ActorTruthCommandBase, frame: AttackTruthFrame): void {
  pending.set(operationId, { encounterId: upper(command.encounter_id), targetFields: writtenTargetFields(frame) });
  if (pending.size > 256) pending.delete(pending.keys().next().value!);
}
/**
 * Land SAVED target truth from an attack frame only when it is NEWER than what the target already holds as
 * confirmed (Codex S3 review F2: ordering is per PARTICIPANT — an older attack's frame arriving after a newer
 * one must neither restore older vitality nor move the target's revision backwards). Banks the target's
 * revision either way (per-field banking is monotonic), stamps the row's revision only when nothing
 * optimistic of the target's own is pending. Returns whether the delta was applied.
 */
const statusRecoveries = new Map<string, Promise<void>>();
/** Saved/replayed raw scalars use the same held projection as the local action. */
function applyAttackTruth(...args: Parameters<typeof applyStoredAttackTruth>): void {
  const [attacker, target, frame] = args;
  const vitality = target ? attackVitalityDelta(target, frame.target) : null;
  applyStoredAttackTruth(...args);
  const raw = frame.attacker as unknown as Row;
  if (raw.aps_total !== undefined) (attacker.encounter_creatures as Row).aps_total = raw.aps_total;
  applyApsScalar(attacker.encounter_creatures as Row, frame.attacker.aps,
    Object.prototype.hasOwnProperty.call(raw, 'aps_used') ? frame.attacker.aps_used : undefined);
  if (target && vitality) ((target.computed ??= {}) as Row).vitality = vitality;
}
const statusProjectionGaps = new Map<string, string>();
const statusProjectionDefinition = (row: Row, encounter: Row, id: string): Row | null =>
  definitionFor(row, encounter, id, 'any') ?? asRow(catalogShell(row, id)) ?? asRow(catalogShell(encounter, id));
function recoverStatusProjection(encounterId: string, reason: string): void {
  statusProjectionGaps.set(encounterId, reason);
  if (statusRecoveries.has(encounterId)) return;
  const loading = Promise.resolve().then(async () => {
    const value = await fetchAndCacheObject(encounterId, { fresh: true });
    if (!value) throw new Error('encounter-not-held');
    await hydrateRosterStatusDefinitions(encounterId, value as Row);
    await handleEncounterUpdate({ type: 'encounter_update', encounter_arr: value } as never);
    statusProjectionGaps.delete(encounterId);
  }).catch(error => console.error('attack:status-projection-recovery', reason, error))
    .finally(() => statusRecoveries.delete(encounterId));
  statusRecoveries.set(encounterId, loading);
}
function projectStatusSnapshot(encounterId: string, row: Row, state: NonNullable<AttackTruthFrame['target']['statuses']> | undefined, local = false): void {
  if (!state) return;
  const encounter = actorEncounterFor(encounterId) as Row | null;
  if (!encounter) {
    row.statuses = mergeAttackStatuses(row, state);
    if (local) Object.defineProperty(row, '_action_computed_unavailable', { value: true, configurable: true, writable: true });
    else recoverStatusProjection(encounterId, 'encounter-not-held');
    return;
  }
  const roster = new Map<string, Row>((Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : [])
    .map((member: Row) => [upper(ftEncounterCreatureField(member.encounter_creatures, 'encounter_creature_id')), member]));
  const reason = projectAttackStatuses(row, state, id => statusProjectionDefinition(row, encounter, id), roster, (member, current) => possessorSceneContext(encounter, member, current));
  if (reason) {
    if (local) Object.defineProperty(row, '_action_computed_unavailable', { value: true, configurable: true, writable: true });
    else recoverStatusProjection(encounterId, reason);
  }
}
function landTargetTruth(encounterId: string, targetId: string, target: MovementTruthRow | null, frame: AttackTruthFrame, targetRev: number | undefined): boolean {
  if (!target || !Number.isInteger(targetRev)) return false;
  const banked = confirmedRevFor(encounterId, targetId, target);
  const newer = targetRev! > banked;
  bankConfirmedRev(encounterId, targetId, target, targetRev!, frame.target.movement ?? {});
  if (newer) {
    applyMaimedProgression(target as Row, frame.paid_save_effects);
    applyMaimedProgression(target as Row, frame.rider_save_effects);
    const ahead = Number(target.encounter_creatures.movement_rev ?? 0) > targetRev!;
    projectStatusSnapshot(encounterId, target as Row, frame.target.statuses);
    applyAttackTruth({ encounter_creatures: {} } as Row, target as Row, ahead && frame.target.movement
      ? { ...frame, target: { ...frame.target, movement: undefined } } : frame);
  }
  if (newer && pendingFor(encounterId, targetId) === 0) target.encounter_creatures.movement_rev = targetRev!;
  return newer;
}
/** Reactions are projected only from saved truth, each reactor ordered on its own revision. */
function landReactionTruth(encounterId: string, states: AttackTruthFrame['reaction_members']): void {
  for (const [id, state] of Object.entries(states ?? {})) {
    const row = actorRowFor(encounterId, id);
    if (!row || state.rev < confirmedRevFor(encounterId, id, row) || state.rev < num(ftEncounterCreatureField(row.encounter_creatures, 'movement_rev'))) continue;
    const vitality = attackVitalityDelta(row as Row, state.ec);
    for (const [key, value] of Object.entries(state.ec)) {
      if (['x_pos', 'y_pos', 'z_pos'].includes(key)) continue;
      if (key === 'reaction' && num(value) === 0) delete row.encounter_creatures[key]; else row.encounter_creatures[key] = value;
    }
    if (state.position && ['x_pos', 'y_pos', 'z_pos'].some(key => key in state.ec)) applyMovementTruth(row, state.position);
    const aps = 'aps' in state.ec ? { aps: num(state.ec.aps) } : {};
    if (aps.aps !== undefined) applyApsScalar(row.encounter_creatures, aps.aps);
    for (const change of state.playerCharactersChanged ?? []) {
      const pc = asRow((row as Row).player_characters);
      if (pc && upper(ftEncounterCreatureField(row.encounter_creatures, 'item_id')) === upper(change.item_id)) pc[change.column] = change.after;
    }
    for (const charge of state.charges) setRowAbilityCharges(row as Row, charge.item_id, charge.current);
    if (vitality) (((row as Row).computed ??= {}) as Row).vitality = vitality;
    projectStatusSnapshot(encounterId, row as Row, state.statuses);
    if (state.position) bankLegacyTruth(encounterId, id, row, state.rev, { ...state.position, ...aps }, 'commit');
    else bankConfirmedRev(encounterId, id, row, state.rev, aps);
    if (pendingFor(encounterId, id) === 0) row.encounter_creatures.movement_rev = state.rev;
    handleCasterUpdate({ type: 'caster_update', caster_arr: row as unknown as CasterArr, local: true });
  }
}
async function post(command: ActorTruthCommandBase): Promise<unknown> {
  markStall('attack-truth:post');
  if (rejectRetiredAttack(command)) throw new MovementTruthTerminal(400, 'unsupported:damage_reaction');
  const response = await saveAppliedTruth(ENDPOINT, command, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(withLogParticipants(command)), signal: AbortSignal.timeout(20000) });
  let result: Row | null = null;
  try { result = await response.json() as Row; } catch { result = null; }
  if (!response.ok && result?.error === 'stale' && Number.isInteger(result.rev)) {
    const row = actorRowFor(command.encounter_id, command.actor_id);
    const position = { x_pos: num(row?.position?.x_pos), y_pos: num(row?.position?.y_pos), z_pos: num(row?.position?.z_pos) };
    throw new MovementTruthRejected({ ok: false, error: 'stale', rev: Number(result.rev), position, ...(Number.isInteger(result.aps) ? { aps: result.aps } : {}), ...(asRow(result.authoritative) ? { authoritative: result.authoritative } : {}) } as MovementTruthRejectedResult);
  }
  if (response.ok && result?.ok === true) return result;
  const message = String(result?.error ?? (response.statusText || 'Attack delivery failed'));
  if (response.status >= 400 && response.status < 500) throw new MovementTruthTerminal(response.status, message, result as Record<string, unknown> | null);
  throw new Error(message);
}
function forgetRejectedAttack(command: ActorTruthCommandBase): void {
  damageFortunateOffers.delete(command.operation_id);
  pending.delete(command.operation_id);
  reloadRefusedEncounter(command.encounter_id, command.operation_id);
}
/** Completion acknowledgments are independent of scalar-frame dedupe and optimistic revisions. */
function acknowledgeFortunate(command: ActorTruthCommandBase, attacker: Row, frame: AttackTruthFrame | null): void {
  if (frame?.outcome?.fortunate_pending !== false || frame.attacker.fortunate_status === undefined) return;
  const observed = frame.attacker.fortunate_status_rev;
  if (!Number.isInteger(observed) || Number(observed) < confirmedRevFor(command.encounter_id, command.actor_id, attacker as never)) return;
  const statuses = (attacker.statuses ??= {}) as Row;
  const entries = Array.isArray(statuses.target) ? statuses.target as Row[] : [];
  statuses.target = entries.filter(s => !isFortunateStatus(s));
  if (frame.attacker.fortunate_status !== null) (statuses.target as Row[]).unshift({ ...frame.attacker.fortunate_status }); // a NEW status first (N4e)
  fortunatePending.correct(fortunateActorKey(command.encounter_id, command.actor_id), command.operation_id);
}

/** R-131 J3 (lane A6 for N5e, 2026-10-02): `ft:hostile-action-committed` once per committed attack (an opportunity attack too) — the page
 *  twin of PHP's ft_combat_after_action_resolved() hook the noise sweep hears (see ability-truth.ts emitHostileActionCommitted). */
const hostileAttackEmitted = new Set<string>();
function emitHostileAttackCommitted(command: ActorTruthCommandBase): void {
  const c = commandOf(command);
  if (c.swing_skipped || hostileAttackEmitted.has(c.operation_id)) return;
  hostileAttackEmitted.add(c.operation_id);
  if (hostileAttackEmitted.size > 512) hostileAttackEmitted.delete(hostileAttackEmitted.values().next().value as string);
  window.dispatchEvent(new CustomEvent('ft:hostile-action-committed', { detail: { lane: c.opportunity_attack ? 'opportunity-attack' : 'attack',
    encounterId: upper(c.encounter_id), casterId: upper(c.actor_id), abilityId: upper(c.ability_id ?? c.weapon_id), targetIds: c.target_id ? [upper(c.target_id)] : [],
    operationId: c.operation_id } }));
}
function accepted(command: ActorTruthCommandBase, result: MovementTruthAcceptedResult): void {
  try {
    if (rejectRetiredAttack({ ...command, ...result } as ActorTruthCommandBase)) { forgetRejectedAttack(command); return; }
    emitHostileAttackCommitted(command);
    const attacker = actorRowFor(command.encounter_id, command.actor_id);
    if (!attacker) return;
    const target = targetRow(command);
    const targetId = upper(commandOf(command).target_id);
    const saved = asRow((result as unknown as Row).frame) as unknown as AttackTruthFrame | null;
    acknowledgeFortunate(command, attacker as Row, saved);
    const targetRev = Number((result as unknown as Row).target_rev);
    bankConfirmedRev(command.encounter_id, command.actor_id, attacker, result.rev, saved ? { aps: Number(saved.attacker.aps) } : {});
    pending.delete(command.operation_id);
    if (!saved) return;
    projectInventorySnapshot(command, saved.inventory_state, saved.inventory_state_rev ?? result.rev);
    projectStrayTruth(command, saved.stray, true);
    const optimistic = Number(attacker.encounter_creatures.movement_rev ?? 0);
    if (optimistic <= result.rev) {
      // Attacker side of the saved frame (the target side is landed by revision below).
      projectStatusSnapshot(command.encounter_id, attacker as Row, saved.attacker.statuses);
      applyAttackTruth(attacker as Row, null, saved);
      (attacker.encounter_creatures as Row).aps = confirmedApsFor(command.encounter_id, command.actor_id, attacker);
      reconcileActorRow(command.encounter_id, command.actor_id, attacker);
    }
    landTargetTruth(command.encounter_id, targetId, target, saved, Number.isInteger(targetRev) ? targetRev : undefined);
    landReactionTruth(command.encounter_id, saved.reaction_members);
    const damageOffers = damageFortunateOffers.get(command.operation_id);
    damageFortunateOffers.delete(command.operation_id);
    render(command, attacker, target);
    if ((result as unknown as Row).replay !== true) projectThrownWeapon(command);
    applyTurnTransition(command.encounter_id, commandOf(command), asRow((result as unknown as Row).turn_transition));
    // OWNER RULING 2026-10-03: swing/reaction luck gains offer Fortunate after saved truth lands, once per recipient.
    const c = commandOf(command), encounter = actorEncounterFor(command.encounter_id);
    const offers = new Set([...(c.fortunate_offers ?? []), ...(damageOffers ?? [])]);
    if (!c.opportunity_attack && !c.rolls.fortunate && saved.attacker.luck !== undefined && Number(saved.attacker.luck) > Number(c.before.luck)
        && encounter && knownPlayerRow(encounter as Row, attacker as Row)) offers.add(command.actor_id);
    if ((result as unknown as Row).replay !== true && offers.size)
      offerFortunate(command.encounter_id, c.movement ? c.target_id : command.actor_id, [...offers]);
  } finally {
    pageLegendaryWindowActions()?.accepted(command.operation_id, result.rev);
  }
}
function rejected(command: ActorTruthCommandBase, result: MovementTruthRejectedResult): void {
  if (rejectRetiredAttack({ ...command, ...result } as ActorTruthCommandBase)) { forgetRejectedAttack(command); return; }
  pageLegendaryWindowActions()?.rejected(command.operation_id);
  fortunatePending.correct(fortunateActorKey(command.encounter_id, command.actor_id), command.operation_id);
  const corrected = applyRawAttackCorrection(command, asRow((result as unknown as Row).authoritative));
  if (!corrected) forgetRejectedAttack(command);
  refusalOf({ status: 409, error: result.error, terminal: true }, corrected ? undefined : 'attack');
  window.dispatchEvent(new CustomEvent('ft:attack-truth-reconciled', { detail: { command, authoritative: { rev: result.rev } } }));
}
/** Current saved participant rows replace only their affected scalars/clusters on a stale save. */
function applyRawAttackCorrection(command: ActorTruthCommandBase, raw: Row | null): boolean {
  // Birth-plan rejection still uses the shared entity-lifecycle recovery seam.
  if (attackEffects(commandOf(command)).some(effect => effect.spawnRows?.writes.length)) return false;
  const a = asRow(raw?.attacker), t = asRow(raw?.target);
  const attacker = actorRowFor(command.encounter_id, command.actor_id), target = targetRow(command);
  if (!a || !t || !attacker || !target || !Number.isInteger(a.movement_rev) || !Number.isInteger(t.movement_rev)) return false;
  pending.delete(command.operation_id); damageFortunateOffers.delete(command.operation_id);
  const actorCurrent = num(a.movement_rev) >= confirmedRevFor(command.encounter_id, command.actor_id, attacker);
  const targetId = commandOf(command).target_id;
  const targetCurrent = num(t.movement_rev) >= confirmedRevFor(command.encounter_id, targetId, target);
  if (actorCurrent) projectStatusSnapshot(command.encounter_id, attacker as Row, a.statuses as AttackTruthFrame['attacker']['statuses']);
  if (targetCurrent) projectStatusSnapshot(command.encounter_id, target as Row, t.statuses as AttackTruthFrame['target']['statuses']);
  const frame = { attacker: a, target: t, outcome: commandOf(command).frame.outcome } as unknown as AttackTruthFrame;
  if (actorCurrent) {
    applyAttackTruth(attacker as Row, targetCurrent ? target as Row : null, frame, { attacker: num(a.movement_rev), ...(targetCurrent ? { target: num(t.movement_rev) } : {}) });
    bankConfirmedRev(command.encounter_id, command.actor_id, attacker, num(a.movement_rev), { aps: num(a.aps) });
  } else if (targetCurrent) applyAttackTruth({ encounter_creatures: {} }, target as Row, frame, { target: num(t.movement_rev) });
  if (targetCurrent) bankConfirmedRev(command.encounter_id, targetId, target, num(t.movement_rev), frame.target.movement ?? {});
  projectInventorySnapshot(command, raw?.inventory_state as AttackTruthFrame['inventory_state'], num(a.movement_rev));
  projectStrayTruth(command, raw?.stray as AttackTruthFrame['stray'], true);
  landReactionTruth(command.encounter_id, raw?.reaction_members as AttackTruthFrame['reaction_members']);
  render(command, attacker, targetCurrent ? target : null);
  return true;
}
function failed(command: ActorTruthCommandBase, info: MovementTruthTerminalInfo): void {
  pageLegendaryWindowActions()?.rejected(command.operation_id);
  if (info.status === 400) fortunatePending.correct(fortunateActorKey(command.encounter_id, command.actor_id), command.operation_id);
  forgetRejectedAttack(command);
  const refusal = refusalOf(info, 'attack');
  const role = String(info.body?.identity_role ?? ''), id = upper(info.body?.identity_id);
  if (info.status === 404 && /^[a-z_]+$/.test(role) && /^[0-9A-F-]{36}$/.test(id))
    window.dispatchEvent(new CustomEvent('ft:unported', { detail: { mode: 'refused', reason: 'attack:target-not-in-encounter',
      detail: { encounter_id: command.encounter_id, actor_id: command.actor_id, caller: role + ':' + id.replace(/-/g, '') } } }));
  window.dispatchEvent(new CustomEvent('ft:attack-truth-refused', { detail: { command, info, refusal } }));
}
function attackEffects(command: WeaponAttackCommand) {
  return [command.status_effects, command.damage_status_effects, command.form_status_effects,
    command.paid_save_effects, command.rider_save_effects, command.reaction_effects].filter(effect => effect !== undefined);
}
/** Observe every member whose decided status projection can change, including its stored caster. */
function attackEffectMembers(command: WeaponAttackCommand): MovementTruthRow[] {
  const effects = attackEffects(command), ids = new Set([command.actor_id, command.target_id,
    ...(command.frame.stray ? [command.frame.stray.target_id] : []), ...Object.keys(command.frame.reaction_members ?? {}),
    ...effects.flatMap(effect => [...Object.keys(effect.ec), ...effect.statusesAdded.flatMap(status => [status.target, status.caster]),
      ...effect.statusesRemoved.map(status => status.target), ...effect.statusStacks.map(status => status.target),
      ...(effect.chargesRemoved ?? []).map(charge => charge.holder)])]);
  for (const effect of effects) for (const removed of effect.statusesRemoved) {
    const holder = actorRowFor(command.encounter_id, removed.target);
    for (const status of ftCreatureStatuses(holder, 'target') as Row[]) if (removed.status_id
      ? upper(statusScalar(status, 'status_id')) === upper(removed.status_id) : statusItemId(status) === upper(removed.status_item_id))
      ids.add(statusCasterId(status));
  }
  return [...ids].flatMap(id => { const member = actorRowFor(command.encounter_id, id); return member ? [member] : []; });
}
/** Decided status writes must reach derived clusters before the applied frame removes their base rows. */
function projectAttackEffectStatuses(command: WeaponAttackCommand): void {
  const effects = attackEffects(command).filter(effect => effect.statusesAdded.length || effect.statusesRemoved.length || effect.statusStacks.length);
  if (!effects.length) return;
  const encounter = actorEncounterFor(command.encounter_id) as Row | null;
  if (!encounter) return;
  const roster = savedAttackMembers(encounter);
  for (const effect of effects) projectAbilityStatuses(effect, roster,
    id => statusProjectionDefinition(roster.get(command.actor_id) ?? encounter, encounter, id),
    (member, current) => possessorSceneContext(encounter, member, current), encounter);
}
function exposeAttackResult(base: ActorTruthCommandBase, attack: WeaponAttackCommand, attackerRev: number, targetRev: number): void {
  const newEntities = takeAppliedEntities(base.operation_id);
  const effects = attackEffects(attack), held = attackEffectMembers(attack) as Row[];
  const chargeDeletes = retireMemberChargePools(held, effects.flatMap(effect => effect.chargesRemoved ?? []));
  const build = () => {
    return memberAppliedFrame({ operation_id: base.operation_id, encounter_id: base.encounter_id,
      actor_id: base.actor_id, rev: base.actor_id === attack.actor_id ? attackerRev : targetRev,
      member_revs: { ...Object.fromEntries(held.map(member => {
        const ec = member.encounter_creatures as Row;
        return [String(ftEncounterCreatureField(ec, 'encounter_creature_id')), Number(ftEncounterCreatureField(ec, 'movement_rev') ?? 0)];
      })), ...Object.fromEntries(Object.entries(attack.frame.reaction_members ?? {}).map(([id, member]) => [id, member.rev])),
        ...(attack.frame.stray ? { [attack.frame.stray.target_id]: attack.frame.stray.rev } : {}),
        ...(attack.target_id ? { [attack.target_id]: targetRev } : {}), [attack.actor_id]: attackerRev } }, held, newEntities(),
      [...effects.flatMap(effect => effect.statusesRemoved.flatMap(status => status.status_id ? [{ table: 'statuses', row_id: status.status_id }] : [])),
        ...chargeDeletes,
        ...(attack.frame.inventory_state ?? []).flatMap(state => state.row === null ? [{ table: 'inventory', row_id: state.inventory_id }] : [])],
      [...appliedChargeRows([...(attack.charges ?? []), ...effects.flatMap(effect => effect.charges)], held, effects.flatMap(effect => effect.chargesRemoved ?? [])),
        ...effects.flatMap(effect => appliedSpawnRows(effect.spawnRows)),
        ...effects.flatMap(effect => appliedStatusRows(effect.statusesAdded, abilityTruthCampaign(actorEncounterFor(base.encounter_id) as Row), effect.statusStacks, held)),
        ...(attack.frame.inventory_state ?? []).flatMap(state => state.row
          ? [{ table: 'inventory', row_id: state.inventory_id, row: asRow(state.row.inventory) ?? state.row }] : [])]);
  };
  exposeAppliedFrame(base, build);
  if (attack !== base) exposeAppliedFrame(attack, () => appliedFrameOf(base)!);
}
registerTruthSchema(WEAPON_ATTACK_SCHEMA, {
  apply: (row, command, rev) => {
    if (rejectRetiredAttack(command)) throw new Error('unsupported:damage_reaction');
    if (commandOf(command).frame.outcome.fortunate_pending) fortunatePending.begin(fortunateActorKey(command.encounter_id, command.actor_id), command.operation_id);
    const target = targetRow(command);
    remember(command.operation_id, command, commandOf(command).frame);
    projectAttackEffectStatuses(commandOf(command));
    projectStatusSnapshot(command.encounter_id, row as Row, commandOf(command).frame.attacker.statuses, true);
    if (target) projectStatusSnapshot(command.encounter_id, target as Row, commandOf(command).frame.target.statuses, true);
    applyAttackTruth(row as Row, target as Row | null, commandOf(command).frame, { attacker: rev });
    projectStrayTruth(command, commandOf(command).frame.stray);
    exposeAttackResult(command, commandOf(command), rev, commandOf(command).target_expected_rev + (writtenTargetFields(commandOf(command).frame).length ? 1 : 0));
  },
  render: (command, row) => render(command, row, targetRow(command)),
  commit: post, accepted, rejected, failed,
  staleMessage: 'Your attack was rejected because a participant changed. Current saved values have been applied.',
  computedRows: command => attackEffectMembers(commandOf(command)),
});

export type WeaponAttackCommitOutcome = 'handled' | 'refused' | null;
const OPPORTUNITY_WALK_SCHEMA = 'janus.movement.opportunity.v1';
interface OpportunityWalkCommand extends ActorTruthCommandBase {
  schema: typeof OPPORTUNITY_WALK_SCHEMA;
  reaction: WeaponAttackCommand;
}
const reactionOf = (command: ActorTruthCommandBase): WeaponAttackCommand => (command as OpportunityWalkCommand).reaction;
const opportunitySaves = new WeakMap<ActorTruthCommandBase, Promise<boolean> | null>();
registerTruthSchema(OPPORTUNITY_WALK_SCHEMA, {
  computedRows: envelope => attackEffectMembers(reactionOf(envelope)),
  apply: (mover, envelope, rev) => {
    const command = reactionOf(envelope), attacker = actorRowFor(envelope.encounter_id, command.actor_id);
    if (rejectRetiredAttack(command as unknown as ActorTruthCommandBase)) throw new Error('unsupported:damage_reaction');
    if (!attacker) throw new Error('Opportunity attacker disappeared');
    // Capture before local apply/paint can enqueue a multi-actor save behind this OA.
    opportunitySaves.set(envelope, actorOutbox(envelope.encounter_id, command.actor_id).settleAppliedSaves());
    remember(command.operation_id, command as unknown as ActorTruthCommandBase, command.frame);
    if (command.rolls.fortunate) fortunatePending.begin(fortunateActorKey(command.encounter_id, command.actor_id), command.operation_id);
    projectAttackEffectStatuses(command);
    projectStatusSnapshot(envelope.encounter_id, attacker as Row, command.frame.attacker.statuses, true);
    projectStatusSnapshot(envelope.encounter_id, mover as Row, command.frame.target.statuses, true);
    applyAttackTruth(attacker as Row, mover as Row, command.frame, { attacker: command.expected_rev + 1, target: rev });
    exposeAttackResult(envelope, command, command.expected_rev + 1, rev);
  },
  render: (envelope, mover) => {
    const command = reactionOf(envelope), attacker = actorRowFor(envelope.encounter_id, command.actor_id);
    if (attacker) render(command as unknown as ActorTruthCommandBase, attacker, mover);
  },
  commit: async envelope => {
    const command = reactionOf(envelope);
    const saves = opportunitySaves.get(envelope);
    if (saves && !await saves) throw new MovementTruthTerminal(409,
      actorOutbox(envelope.encounter_id, command.actor_id).blocked() ? 'watcher-missing-or-busy' : 'Earlier actor save was refused');
    try {
      const result = await post(command as unknown as ActorTruthCommandBase) as Row;
      return { ...result, rev: result.target_rev, attack_result: result, position: asRow(asRow(result.frame)?.target)?.movement };
    } catch (error) {
      if (!(error instanceof MovementTruthRejected)) throw error;
      const result = error.result as unknown as Row, target = asRow(asRow(result.authoritative)?.target);
      const position = asRow(target?.movement);
      if (!target || !position || !Number.isInteger(target.movement_rev)) throw error;
      throw new MovementTruthRejected({ ...error.result, rev: Number(target.movement_rev), position: position as unknown as MovementTruthRejectedResult['position'], attack_result: result } as MovementTruthRejectedResult);
    }
  },
  // Settle any externally queued movement work, including a forced move's exits.
  accepted: (envelope, result) => {
    try { accepted(reactionOf(envelope) as unknown as ActorTruthCommandBase, (result as unknown as Row).attack_result as MovementTruthAcceptedResult); }
    finally { settleWalkZoneWork(envelope.operation_id, envelope.encounter_id, envelope.actor_id, result as unknown as Row); }
  },
  rejected: (envelope, result) => { dropQueuedZoneExits(envelope.operation_id); rejected(reactionOf(envelope) as unknown as ActorTruthCommandBase, (result as unknown as Row).attack_result as MovementTruthRejectedResult); },
  failed: (envelope, info) => { dropQueuedZoneExits(envelope.operation_id); failed(reactionOf(envelope) as unknown as ActorTruthCommandBase, info); },
  staleMessage: 'Your move and opportunity reaction were rejected because a participant changed. Reloading the encounter.',
});

/** Read-only Janus context for shared attack geometry over the page's held roster. */
function opportunityFlankContext(encounter: Row): JanusCtx {
  const members = new Map<string, Row>();
  for (const raw of Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) {
    const row = asRow(raw), id = upper(asRow(row?.encounter_creatures)?.encounter_creature_id);
    if (row && id) members.set(id, row);
  }
  const has = (row: Row | null, key: string): boolean => !!row && (ftLayerValue(row, key) != null || ftLayerEntries(row, key).length > 0);
  return { encounter, members, read: {
    value: (row: Row | null, key: string) => row ? ftLayerValue(row, key) : null,
    has, entries: (row: Row | null, key: string) => row ? ftLayerEntries(row, key) : [],
    statuses: (row: Row) => ftCreatureStatuses(row, 'target') as Row[],
    definition: (id: string) => definitionFor(null, encounter, id, true),
  } } as unknown as JanusCtx;
}
/** Residual status/death events outside the shared post-damage reaction frame. */
const A2_TARGET_TRIGGER_KEYS = ['triggers_on_self_zero_vitality',
  'triggers_when_caster_gains_status_effect', 'triggers_when_caster_loses_status_effect'];
const A2_ATTACKER_TRIGGER_KEYS = ['triggers_when_target_gains_status_effect'];
/** Ordinary clicks on an unlocked sleeper use the full ability pipeline. OA consequences reuse its native wake helper in the damage frame.
 * A definition the page does not hold counts as "may wake". */
function a2SleepingTarget(encounter: Row, target: Row): boolean {
  const statuses = ftCreatureStatuses(target, 'target') as Row[];
  const itemOf = (s: Row): string => upper(scalar(s.status_item_id) ?? ftItemsCol(asRow(s.status_arr), 'item_id'));
  if (!statuses.some(s => itemOf(s) === '095BC6BC-06F6-74E0-2B8A-FC1F32AF809B')) return false;
  // only a CERTAIN lock keeps A2 (it never wakes): a truthy lock on the row, or on a status that is plainly payload — not a self-anchored
  // aura (creature_payload_statuses drops it) and not owner-gated; anything uncertain steps aside to the pipeline (Codex step-18 review P2)
  const truthy = (v: unknown): boolean => { const x = scalar(v); return x != null && x !== '' && x !== 0 && x !== '0' && x !== false; };
  if (truthy(ftLayerValue(target, 'sleeping_not_ended_by_damage'))) return false;
  const targetId = upper(asRow(target.encounter_creatures)?.encounter_creature_id);
  for (const s of statuses) {
    const def = definitionFor(target as MovementTruthRow, encounter, itemOf(s), true) ?? asRow(s.status_arr);
    if (!def) return true;
    if (!truthy(ftLayerValue(def, 'sleeping_not_ended_by_damage'))) continue;
    const selfAnchor = Number(scalar(s.status_aoe_source) ?? 0) === 1 && upper(scalar(s.status_caster_id)) === targetId;
    if (selfAnchor || ftLayerValue(def, 'if_aura_owner_has_status_effect_active') != null) return true;
    return false;
  }
  return true;
}
/** R-150: ordinary clicks use the full ability contest pipeline; OA consequences reuse its native retry helper in the damage frame. */
function a2ContestRetryTarget(encounter: Row, target: Row): boolean {
  const statuses = ftCreatureStatuses(target, 'target') as Row[];
  const truthy = (v: unknown): boolean => { const x = scalar(v); return x != null && x !== '' && x !== 0 && x !== '0' && x !== false; };
  for (const s of statuses) {
    const itemId = upper(scalar(s.status_item_id) ?? ftItemsCol(asRow(s.status_arr), 'item_id'));
    const def = definitionFor(target as MovementTruthRow, encounter, itemId, true) ?? asRow(s.status_arr);
    if (def && truthy(ftLayerValue(def, 'target_retries_contesting_roll_on_damage_taken'))) return true;
  }
  return false;
}
/** A quiet OA that cannot floor its mover has no deferred death-tail status gain.
 * Save/reaction statuses use the shared damage frame, which preflights their own gain listeners. */
const A2_STATUS_GAIN_KEYS = new Set(['triggers_when_caster_gains_status_effect', 'triggers_when_target_gains_status_effect']);
/** Playtest 2026-10-15 (Wizard Guy's dagger on Mage Warrior): a gain carrier is a predicate on the NAMED status
 *  (janus_trigger_entry_predicate_match), so A2 steps aside only for one naming a status its OWN tails can apply. Those tails are the
 *  commit's server death tail — apply_player_dying (Crippled, Dying, Maimed; a PC at 0, caster = the attacker), apply_death_effect on a kill
 *  (Prone + Rotting self-cast on the corpse; Maimed on a PC, caster = the attacker) and Maimed's overflow to Dead — plus the Maimed crit-cascade
 *  save. Fortunate, Shielded and a damage reaction's statuses ride engine paths that run their own gain fan-out (apply-status-effect.ts);
 *  status-applying attack abilities / weapon layers are refused by explicitWeaponFamilyRefusal and on-hit tails step aside (k4OnHitStepAside).
 *  Unyielding Command's Stunned / Dazed can never fire on a swing and no longer step it aside. */
const A2_TAIL_STATUS_IDS: ReadonlySet<string> = new Set([...PC_DYING_STATUS_IDS,
  'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA', '62E83DEB-4569-AE3A-F671-FF514C508FD2', '9FEE2E8A-9CF7-D74D-5D16-B86C2603A1FA']); // Prone, Rotting, Dead
const A2_STATUS_GAIN_NAMED: Readonly<Record<string, ReadonlySet<string>>> = Object.fromEntries([...A2_STATUS_GAIN_KEYS].map(k => [k, A2_TAIL_STATUS_IDS]));
function attackTriggerReaders(encounter: Row, attacker: Row, target: Row = attacker) {
  const held = (id: string): Row | null => definitionFor(attacker, encounter, id, 'any') ?? definitionFor(target, encounter, id, 'any');
  return pageTriggerReaders({ encounter, definition: held, entries: (row, key) => row ? ftLayerEntries(row, key) : [],
    value: (row, key) => row ? ftLayerValue(row, key) : null, itemField: (row, key) => row ? ftItemsCol(row, key) : null });
}
function a2ReactionStepAside(encounter: Row, attacker: Row, target: Row,
  opts: { noTargetStatusGain?: boolean } = {}): string | null {
  const read = attackTriggerReaders(encounter, attacker, target);
  const keep = (keys: string[]): string[] => opts.noTargetStatusGain ? keys.filter(k => !A2_STATUS_GAIN_KEYS.has(k)) : keys;
  try {
    const onTarget = authoredTriggerKey(read, target, keep(A2_TARGET_TRIGGER_KEYS), A2_STATUS_GAIN_NAMED);
    if (onTarget) return `reaction:target:${onTarget}`;
    const onAttacker = authoredTriggerKey(read, attacker, keep(A2_ATTACKER_TRIGGER_KEYS), A2_STATUS_GAIN_NAMED);
    if (onAttacker) return `reaction:attacker:${onAttacker}`;
    return null;
  } catch (error) {
    return 'reaction:unknown:' + (error instanceof TriggerInputUnavailable ? error.reason : String(error instanceof Error ? error.message : error).slice(0, 60));
  }
}

/** Lane RX S2c: could the mover's OA retaliation fire? ('none' | a hand-back reason). The janus_trigger() twin over the page's rows. */
function opportunityRetaliationVerdict(encounter: Row, mover: Row, watcher: Row, statusesMayChange: boolean): string {
  const held = (id: string): Row | null => definitionFor(mover, encounter, id, 'any');
  const read = pageTriggerReaders({ encounter, definition: held, entries: (row, key) => row ? ftLayerEntries(row, key) : [],
    value: (row, key) => row ? ftLayerValue(row, key) : null, itemField: (row, key) => row ? ftItemsCol(row, key) : null });
  const event = { trigger: 'triggers_on_opportunity_attack_against' };
  try {
    const { caster, carriers } = collectCarrierCandidates(read, mover, event);
    const candidates = [...carriers, ...collectStatusCandidates(read, caster, event)];
    return candidates.some(c => {
      const decision = gateTriggerCandidate(read, c, caster, event, watcher);
      return decision.fire || (statusesMayChange && (decision.gate === 'passive-inactive' || decision.gate === 'passive-conditions'));
    }) ? 'mover-retaliation' : 'none';
  } catch (error) {
    return error instanceof TriggerInputUnavailable ? 'mover-retaliation:' + error.reason : 'mover-retaliation-unknown';
  }
}

/** Movement reactions share the mover's outbox; each swing's actual death rows share its move envelope.
 * Session A step 12 (2026-09-30): `forced` = a GM FORCE MOVE (force-move-truth.ts) — a two-point move with no pricing whose leave point is
 * the mover's START (final_creature_move's `$mover_snapshot_for_leave`); the same watcher / damage-reaction / retaliation gates, the move
 * and the OA committed together on the same envelope. */
// Reader faults before preparation report the unsupported local slice. Once preparation starts,
// failures abort the same action; the suspended movement owns its original draw sequence.
registerMovementReactionPlanner(function* (input, encounter, mover, watcherId, forced, geometry) {
  const stage = { committing: false };
  try { return yield* planOpportunityWalk(input, encounter, mover, watcherId, forced, stage, geometry); }
  catch (error) { return opportunityWalkFault(error, stage, watcherId); }
});
registerMovementReactionResolver((input, encounter, mover, watcherId, forced, geometry) => {
  const stage = { committing: false };
  try { return resolveOpportunityWalk(input, encounter, mover, watcherId, forced, stage, geometry); }
  catch (error) {
    return opportunityWalkFault(error, stage, watcherId);
  }
});
function opportunityWalkFault(error: unknown, stage: { committing: boolean }, watcherId: string): null {
  if (stage.committing || error instanceof MovementRefused || isWalkRetryAfterLoad(error)) throw error;
  const reason = 'reaction-threw:' + (error instanceof Error ? `${error.name}:${error.message}` : String(error));
  console.warn('attack-truth: opportunity walk refused after a pre-draw fault', error);
  window.dispatchEvent(new CustomEvent('ft:action-slice-unavailable', { detail: { schema: OPPORTUNITY_WALK_SCHEMA, reason, watcherId } }));
  return null;
}
function resolveOpportunityWalk(input: PlainWalkCommitInput, encounter: Row, mover: MovementTruthRow, watcherId: string,
  forced: ForcedOpportunityMove | undefined, stage: { committing: boolean }, walkGeometry?: WalkReactionGeometry): PlainWalkResult | null | Promise<PlainWalkResult | null> {
  return resumeWalkPlan(planOpportunityWalk(input, encounter, mover, watcherId, forced, stage, walkGeometry));
}
function* planOpportunityWalk(input: PlainWalkCommitInput, encounter: Row, mover: MovementTruthRow, watcherId: string,
  forced: ForcedOpportunityMove | undefined, stage: { committing: boolean }, walkGeometry?: WalkReactionGeometry): Generator<Promise<unknown>, PlainWalkResult | null, unknown> {
  const committed = new Set<string>();
  if (forced) return yield* planOpportunityStrike(input, encounter, mover, watcherId, forced, stage, walkGeometry, committed);
  const read = { ...movementScopeReaders(encounter), ...walkGeometry, ...walkZoneReaders(input.encounterId, input.actorId) };
  const consent = (input as WalkReactionConsentInput).triggerConsent;
  const pending = opportunityLeaveWatchers(input.actorId, input.origin, input.route, encounter, mover, read).map(id => {
    // This frame owns every offered OA, including declines. Its route checks must not
    // mistake another scheduled swing for an unhandled reach listener.
    committed.add(id);
    const watcher = actorRowFor(input.encounterId, id);
    return { id, step: watcher ? opportunityReachExit(input.origin, input.route, watcher, mover, read)?.step ?? 0 : 0 };
  }).filter(({ id }) => consent?.answer(id, OPPORTUNITY_ATTACK_ID) !== false).sort((a, b) => a.step - b.step);
  // Keep the scope's offered watcher even if a conservative geometry reader could not enumerate it.
  if (!committed.has(upper(watcherId))) { committed.add(upper(watcherId)); pending.unshift({ id: upper(watcherId), step: 0 }); }
  const originalInput = input;
  const pricingMover = { ...mover, position: { ...asRow(mover.position) }, encounter_creatures: { ...mover.encounter_creatures } };
  let paidSteps = 0, paidCost = 0;
  const price = (walk: PlainWalkResult, steps?: number): PlainWalkResult => {
    if (steps !== undefined && paidSteps + steps === 0) return { ...walk, speed_cost: 0,
      updates: { ...walk.updates, movement_pct: Number(ftEncounterCreatureField(pricingMover.encounter_creatures, 'movement_pct') ?? 0),
        aps: Number(ftEncounterCreatureField(pricingMover.encounter_creatures, 'aps') ?? 0) } };
    const route = steps === undefined ? originalInput.route : cutRouteAtFootIndex(originalInput.route, paidSteps + steps);
    const endpoint = route[route.length - 1]!;
    const scope = resolvePlainWalkScope({ ...originalInput, route,
      destination: { x: endpoint[0]!, y: endpoint[1]!, z: endpoint[2] ?? originalInput.origin.z } }, encounter, pricingMover, read, committed);
    if (!scope.supported) throw new MovementRefused('The opportunity walk prefix could not be priced.');
    return { ...walk, speed_cost: (scope.walk.speed_cost ?? 0) - paidCost,
      updates: { ...walk.updates, movement_pct: scope.walk.updates.movement_pct, aps: scope.walk.updates.aps } };
  };
  let last: PlainWalkResult | null = null;
  const route: PlainWalkResult['route'] = [];
  let before: PlainWalkResult['before'], cost = 0;
  for (let i = 0; i < pending.length; i++) {
    const held = actorRowFor(input.encounterId, input.actorId);
    if (!held) return null;
    const sequence: OpportunityWalkSequence | undefined = pending.length > 1 ? { more: i + 1 < pending.length, price } : undefined;
    last = yield* planOpportunityStrike(input, encounter, held, pending[i]!.id, undefined, stage, walkGeometry, committed, sequence);
    if (!last) return null;
    before ??= last.before;
    cost += last.speed_cost ?? 0;
    route.push(...last.route.slice(route.length ? 1 : 0));
    if (!sequence) return last;
    if (!sequence.next) break; // terminal damage or a reached zone stops the remaining swings and feet
    // The saved attack can advance a participant beyond its optimistic revision.
    // The next OA must draw from that saved prefix, including its vitality and bank.
    const [saved] = (yield waitForWalkPrefixSave(Promise.all([sequence.saved!, waitForActorMovement(input.encounterId, input.actorId)]))) as [unknown, void];
    if (!isMovementTruthAcceptedResult(saved)) throw new MovementRefused('The opportunity walk prefix could not be saved.');
    paidSteps += sequence.steps ?? 0;
    paidCost = cost;
    const savedMover = actorRowFor(input.encounterId, input.actorId);
    const savedEncounter = actorEncounterFor(input.encounterId);
    if (!savedMover || !savedEncounter) return null;
    encounter = savedEncounter;
    const [x, y, z] = read.position(savedMover);
    input = { ...sequence.next, origin: { x, y, z },
      route: sequence.next.route.map((p, index) => index ? p : [x, y, z]) };
  }
  return last ? { ...last, origin: { ...last.origin,
    x: route[0]![0], y: route[0]![1], z: route[0]![2] ?? last.origin.z }, before, route, speed_cost: cost } : null;
}
interface OpportunityWalkSequence { more: boolean; next?: PlainWalkCommitInput; steps?: number; saved?: Promise<unknown>;
  price: (walk: PlainWalkResult, steps?: number) => PlainWalkResult }
/** The OA swing's entire reached walk shares one entry ledger. Ordinary contacts keep their legacy history. */
function opportunityZoneEntryRecords(encounter: Row, walk: PlainWalkResult, halted: PlainWalkResult): ReturnType<typeof readPaoeEntryRecords> | undefined {
  const entries = [walk, halted].flatMap(candidate => [
    ...(candidate.zone_entries ?? []),
    ...(candidate.follower_zone_entries ?? []).flatMap(follower => follower.entries),
  ]);
  if (!entries.length) return undefined;
  const read = abilityTruthReaders({}, encounter, new Set());
  const surfaceOnly = entries.every(entry => hasSurfaceTypeValue(read, read.definition(entry.originId)));
  // OA entry resolution only reads these facts. Both readers provide held records;
  // adoption replaces legacy records, while surface reads return a fresh rows list.
  if (surfaceOnly) return readSurfaceEntryRecords(encounter);
  const legacy = readPaoeEntryRecords(encounter);
  return entries.some(entry => hasSurfaceTypeValue(read, read.definition(entry.originId)))
    ? combinePaoeEntryRecords(legacy, readSurfaceEntryRecords(encounter)) : legacy;
}
function* planOpportunityStrike(input: PlainWalkCommitInput, encounter: Row, mover: MovementTruthRow, watcherId: string,
  forced: ForcedOpportunityMove | undefined, stage: { committing: boolean }, walkGeometry: WalkReactionGeometry | undefined,
  committed: Set<string>, sequence?: OpportunityWalkSequence): Generator<Promise<unknown>, PlainWalkResult | null, unknown> {
  const unavailable = (reason: string): null => {
    window.dispatchEvent(new CustomEvent('ft:action-slice-unavailable', { detail: { schema: OPPORTUNITY_WALK_SCHEMA, reason, watcherId } }));
    return null;
  };
  const watcher = actorRowFor(input.encounterId, upper(watcherId));
  if (!watcher || actorOutbox(input.encounterId, upper(watcherId)).pending({ includeAppliedSaves: false }) > 0
    || actorOutbox(input.encounterId, upper(watcherId)).blocked()) return unavailable('watcher-missing-or-busy');
  const packet = asRow(asRow(asRow(watcher.computed)?.creature)?.opportunity_attack);
  const mechanics = asRow(packet?.mechanics);
  if (packet?.schema !== 'ft.opportunity-input.v1' || packet.supported !== true || !mechanics) return unavailable('reaction-input:' + String(packet?.reason ?? 'missing'));
  // Full-route and halt-prefix checks retain the scene geometry and zone readers used by the provoking walk.
  const moveRead = { ...movementScopeReaders(encounter), ...walkGeometry, ...walkZoneReaders(input.encounterId, input.actorId) };
  // Reaction geometry: one convex room. PROPS (lane RX S2a, 2026-09-28) never reach a melee swing's PHP spatial context —
  // ft_spatial_action_geometry_context() gives melee cover 'none' and tests line of effect against the SHAPE only, and the leave
  // point is inside the watcher's reach (touch-located). What a prop CAN change is the mover's height (a support surface), so a
  // room with props requires a floor-level, non-traversal route (checked on the resolved walk below). Separate rooms and
  // status-driven damage/speed changes still need their own interruption/line-of-effect ports.
  if (!Array.isArray(encounter.encounter_inventories_arr)) return unavailable('room-inventory-unheld');
  let shape = asRow(encounter.encounters)?.encounter_shape ?? encounter.encounter_shape;
  if (typeof shape === 'string') { try { shape = JSON.parse(shape); } catch { return null; } }
  const boxes = asRow(shape)?.boxes;
  if (!Array.isArray(boxes) || boxes.length !== 1) return unavailable('reaction-room-shape');
  const box = asRow(boxes[0]), bx = asRow(box?.x), by = asRow(box?.y);
  const wp = moveRead.position(watcher);
  if (!bx || !by || wp[0] < Number(bx.min) || wp[0] > Number(bx.max)
    || wp[1] < Number(by.min) || wp[1] > Number(by.max) || wp[2] !== Number(box?.floor)) return unavailable('watcher-room');
  // OWNER RULING 2026-10-02 (optional triggers are prompt-then-resolve) — the CROSS-LANE SEAM for MV3 / MV4 (lane A11): the walk page asks
  // the controllers FIRST (opportunityConsentQuestions + askTriggerConsent) and passes the answered reader as `input.triggerConsent`. A
  // player-controlled watcher whose controller did not say yes makes NO opportunity attack: the walk commits plain. No reader (a walk page
  // that does not ask yet) = PHP's auto-fire, as before. A GM FORCE MOVE keeps the auto-fire (named residual: the force-move page does not ask).
  const consent = forced ? undefined : (input as WalkReactionConsentInput).triggerConsent;
  const allowed = (row: Row, abilityId: string): boolean => {
    if (!consent || !consent.controller(row)) return true;
    return consent.answer(upper(asRow(row.encounter_creatures)?.encounter_creature_id), abilityId) === true;
  };
  let walk: PlainWalkResult;
  committed.add(upper(watcherId));
  // a walk committed without a swing: its zone entries' real plans, then the ordinary walk commit (they commit once the move is saved)
  const commitWalkPlainly = function* (): Generator<Promise<unknown>, PlainWalkResult | null, unknown> {
    const planned = yield* awaitWalkPlan(planWalkZoneEntries(input.encounterId, input.actorId, walk));
    if (planned.kind === 'handback') return unavailable('movement:persistent-area:' + planned.reason);
    stage.committing = true;
    return yield* awaitWalkPlan(commitPlannedPlainWalk(input, mover, planned.plan));
  };
  // Forced moves and OOC walks carry the held bank unchanged, including any terminal prefix.
  const bank = forced || !moveRead.inCombat(encounter, mover) ? {
    movement_pct: Number(ftEncounterCreatureField(mover.encounter_creatures, 'movement_pct') ?? 0),
    aps: Number(ftEncounterCreatureField(mover.encounter_creatures, 'aps') ?? 0),
  } : undefined;
  if (bank && ![bank.movement_pct, bank.aps].every(Number.isFinite)) return unavailable('movement:force-bank-unheld');
  const withUnpricedBank = (result: PlainWalkResult): PlainWalkResult => bank
    ? { ...result, before: bank, updates: { ...result.updates, ...bank } } : result;
  if (forced) {
    walk = withUnpricedBank({ origin: { ...forced.origin }, updates: { ...forced.updates }, route: forced.route.map(p => [...p]) as never,
      traversal: false } as unknown as PlainWalkResult);
  } else {
    const scope = resolvePlainWalkScope(input, encounter, mover, moveRead, committed);
    if (!scope.supported) return unavailable('movement:' + ('reason' in scope ? scope.reason : scope.message));
    walk = withUnpricedBank(scope.walk);
    if (!walk.before) return unavailable('movement:missing-before');
    // the watcher's controller declined (or let the 5 s pass): the walk goes ahead with no opportunity attack — nothing drawn, nothing spent
    if (!allowed(watcher as Row, OPPORTUNITY_ATTACK_ID)) {
      stage.committing = true;
      return yield* awaitWalkPlan(tryCommitPlainWalk(input));
    }
    // The combined frame judges reached contacts around the actual strike. A synthetic
    // full-route halt cannot reject a contact beyond a terminal OA's paid prefix.
    const zoneRefusal = preflightWalkZoneEntries(input.encounterId, input.actorId, walk, true);
    if (zoneRefusal) return unavailable('movement:persistent-area:' + zoneRefusal);
  }
  const wg = moveRead.geometry(watcher), mg = moveRead.geometry(mover);
  if (!wg || !mg) return null;
  const floorZ = Number(box?.floor);
  if (encounter.encounter_inventories_arr.length && (walk.traversal || walk.origin.z !== floorZ
    || walk.route.some(p => Number(p[2]) !== floorZ))) return unavailable('reaction-route-off-floor');
  // The packet proves admission; the held watcher retains the other compiled clusters
  // needed when it also cast a reached zone in this shared frame.
  const hookLayers = { ...asRow(mechanics.item_layer_types) };
  for (const [key, entries] of Object.entries(hookLayers)) if (Array.isArray(entries)) {
    const ids = new Set(entries.map(abilityEntryId));
    hookLayers[key] = [...ftLayerEntries(watcher, key)].filter(entry => ids.has(abilityEntryId(entry)));
  }
  const attacker = { ...watcher, ...mechanics, computed: watcher.computed, statuses: watcher.statuses, item_layer_types: hookLayers,
    encounter_creatures: { ...watcher.encounter_creatures }, position: { ...asRow(watcher.position) } } as MovementTruthRow;
  const read = readers(attacker, encounter);
  read.opposingTeams = moveRead.opposingTeams;
  read.distanceFt = (a, b) => {
    const source = moveRead.position(a), target = moveRead.position(b);
    return movementReachDistanceFt(wg, { x: source[0], y: source[1], z: source[2] }, mg, { x: target[0], y: target[1], z: target[2] });
  };
  const weaponId = upper(packet.weapon_id);
  if (ftLayerEntries(mover, 'ability_does_not_provoke_opportunity_attacks').length || mover.ability_does_not_provoke_opportunity_attacks) return null;
  // ft_creature_move_provokes_opportunity_attacks(): find_caster_layer_provider_arr() over EVERY ability bucket and a feat's grants (a
  // quick_ability like Storm Gallop protects too) — a mover that may be protected hands back (Codex step-12 review P1)
  // A15 (0a, 2026-10-08): casterMayProvide walks the mover's payload STATUSES too (statusLayerProviders) — this reader lacked `statuses`, so
  // every walk out of a watcher's reach threw `read.statuses is not a function` (no POST, the standee snapped back)
  if (casterMayProvide({ value: (r: Row, k: string) => ftLayerValue(r, k), entries: (r: Row, k: string) => ftLayerEntries(r, k) ?? [],
    statuses: (r: Row) => (ftCreatureStatuses(r, 'target') ?? []) as Row[],
    resolveItem: (entry: unknown) => { const id = upper(typeof entry === 'string' ? entry : ftItemsCol(asRow(entry), 'item_id') ?? asRow(entry)?.item_id);
      return definitionFor(mover, encounter, id, true) ?? null; } } as never, mover as Row, 'ability_does_not_provoke_opportunity_attacks'))
    return unavailable('mover-oa-protection-possible');
  // The shared weapon damage frame classifies the mover's status layers and preflights their consequences before drawing.
  // Lane RX S2c: an authored OA retaliation (`triggers_on_opportunity_attack_against`) is decided by the janus_trigger() twin below,
  // after the damage-reaction plan — a carrier the gates refuse (reaction spent, charges, opt-out, out of range…) no longer hands back.
  let moverAuthorsRetaliation = false;
  for (const entry of carrierEntries(mover)) {
    const definition = carrierDefinition(mover, encounter, entry);
    if (!definition || ftLayerEntries(definition, 'ability_does_not_provoke_opportunity_attacks').length) return unavailable('mover-carrier-or-protection');
    if (ftLayerEntries(definition, 'triggers_on_opportunity_attack_against').length || ftLayerValue(definition, 'triggers_on_opportunity_attack_against') != null) moverAuthorsRetaliation = true;
    if ([EVADE_ID, 'B2BC3219-C745-7050-51DD-892951F0026B', FORTUNATE_ID].includes(upper(ftItemsCol(definition, 'item_id')))) continue;
    if (['triggers_when_caster_takes_any_damage', 'triggers_when_caster_takes_damage_type'].some(key => ftLayerEntries(definition, key).length)) continue;
    if (['reaction_ability', 'opportunity_attack_ability'].includes(String(ftItemsCol(definition, 'item_type')))
      && num(mover.encounter_creatures.reaction) <= 0) continue;
    if (Object.keys(asRow(definition.item_layer_types) ?? {}).some(key => key.startsWith('triggers_') && !INACTIVE_MOVER_OA_TRIGGERS.has(key))) return unavailable('mover-trigger:' + String(ftItemsCol(definition, 'item_id')));
  }
  const passiveFailure = yield* awaitWalkPlan(hydrateAttackPassiveDefinitions(watcher as Row, encounter,
    findInventoryRow(watcher as Row, weaponId) ?? definitionFor(watcher as Row, encounter, weaponId)));
  if (passiveFailure) return unavailable(passiveFailure);
  if (moverAuthorsRetaliation) {
    // fire_opportunity_attack_triggers()'s TARGET arm: janus_trigger('triggers_on_opportunity_attack_against', caster: the mover,
    // inciting: the watcher) after the OA's phases. Judged on the mover as it stands NOW — a miss leaves its reaction unspent, and
    // every gate only narrows as the swing spends things — so "nothing can fire here" holds for every outcome of the draw.
    // A damage cleanse that can fire removes statuses → the damage frame's derived flush can re-activate a condition-gated passive
    // (Codex S2 P1) — then a passive refused only on its activation stamp / conditions may fire after all.
    const verdict = opportunityRetaliationVerdict(encounter, mover as Row, watcher as Row, true);
    if (verdict !== 'none') return unavailable(verdict);
  }
  const cascades = read.critCascadeRules(encounter, attacker);
  if (read.critVariant(encounter, attacker) !== 'maximum' || read.explodingDiceActive(encounter, attacker, mover)
    || cascades.remarkable || cascades.worldShaking || cascades.maimed) return unavailable('reaction-house-rules');
  // The shared weapon frame decides lethal hits too. Extra incoming fragments still need their combined movement plan.
  if (num(ftLayerValue(mover, 'all_incoming_damage_changed_by_percentage')) > 0) return unavailable('reaction-incoming-damage-increase');
  for (const raw of ftLayerEntries(mover, 'passive_ability')) {
    const definition = carrierDefinition(mover, encounter, raw);
    if (!definition) return unavailable('reaction-mover-passive-unresolved');
    if (upper(scalar(ftLayerValue(definition, 'roll_type'))) === EXTRA_DAMAGE_ROLL_TAKEN) return unavailable('reaction-mover-extra-damage-taken');
  }
  const path = resamplePolyline(walk.route.map(p => [p[0], p[1]]), 1);
  let target: MovementTruthRow | null = null, leaveIndex = 0;
  const at = (p: number[]): MovementTruthRow => ({ ...mover, position: { ...asRow(mover.position), x_pos: p[0], y_pos: p[1], z_pos: walk.origin.z }, encounter_creatures: { ...mover.encounter_creatures } });
  if (!forced) {
    const exit = opportunityReachExit(walk.origin, walk.route, watcher, mover, moveRead);
    if (exit) {
      if (!exit.startsInReach) return unavailable('reaction-origin-outside-reach');
      if (exit.endsInReach) return unavailable('reaction-returns-to-reach');
      target = at([...exit.point]); leaveIndex = exit.step;
    }
  }
  if (forced) {
    // the leave point is the START (PHP fires the leave dispatch at the pre-move snapshot); the landing must be out of reach — the commit's own
    // reaction authority (attack_truth_commit_inc.php: before ≤ reach < updates on ft_combat_center_distance_ft) judged here, before a die
    const end = { ...mover, position: { ...asRow(mover.position), x_pos: forced.updates.x_pos, y_pos: forced.updates.y_pos, z_pos: forced.updates.z_pos }, encounter_creatures: { ...mover.encounter_creatures } } as MovementTruthRow;
    const startAt = { ...mover, position: { ...asRow(mover.position), x_pos: forced.origin.x, y_pos: forced.origin.y, z_pos: forced.origin.z }, encounter_creatures: { ...mover.encounter_creatures } } as MovementTruthRow;
    if (!(read.distanceFt(attacker, startAt) <= read.reachFt(attacker)) || !(read.distanceFt(attacker, end) > read.reachFt(attacker))) return unavailable('reaction-force-not-a-leave');
    target = startAt;
  }
  if (!target) { if (forced) return unavailable('reaction-force-not-a-leave'); return yield* commitWalkPlainly(); } // The resolved walk never reaches the edge.
  // PACK TACTICS (Codex S2 P1): apply_pack_tactics_adv_to_attack_roll() adds an ADV stack whenever ANY living ally of the watcher
  // is within reach of the mover. The packet PROVES the watcher lacks it (`positional.pack_tactics === false`, S2 producer); a packet
  // without that proof keeps the old guard: any watcher ally within its reach of the leave point hands back.
  // VISIBILITY (Codex S2 P1): a mover the watcher cannot SEE is touch-located at this range and PHP adds a DIS stack
  // (attack_roll.php); the A2 resolver models only a seen target, so anything but 'visible' hands back.
  const flankCtx = opportunityFlankContext(encounter);
  let packAllyInReach = false, sight: ReturnType<typeof abilityVisibility>, elevationStacks = 0;
  let flankingSource: ReturnType<typeof weaponAttackFlankingSource>;
  try {
    flankingSource = weaponAttackFlankingSource(flankCtx, watcher as Row, target as Row);
    // the swing's ELEVATION stack (weapon_attack_skill_roll_of_damage_roll → apply_elevation_adv_dis_to_attack_roll), judged at the leave
    // point — D-012 (owner 2026-09-30, full 3D reach near climbs / ledges): folded into the A2 swing, no longer a hand-back. An unprovable
    // body height still throws (→ reaction-positional-unknown).
    elevationStacks = attackElevationAdv(flankCtx, watcher as Row, target as Row, true);
    if (asRow(packet.positional)?.pack_tactics !== false) {
      // encounter_row_team_uuid(): a bare id or an {items:{item_id}} shell.
      const teamOf = (row: Row): string => { const raw = asRow(row.encounter_creatures)?.team, shell = asRow(raw); return upper(shell?.items ? asRow(shell.items)?.item_id : scalar(raw)); };
      const watcherTeam = teamOf(watcher as Row);
      for (const ally of flankCtx.members.values()) {
        const allyEc = asRow(ally.encounter_creatures);
        const id = upper(allyEc?.encounter_creature_id);
        if (!id || id === upper(watcherId) || id === upper(input.actorId) || teamOf(ally) !== watcherTeam || num(allyEc?.encounter_creature_dead) === 1) continue;
        if (rulesWithinReach(flankCtx, ally, target as Row)) { packAllyInReach = true; break; }
      }
    }
    sight = abilityVisibility(encounter, watcher as Row, target as Row);
  } catch (error) { return unavailable('reaction-positional-unknown' + (error && typeof error === 'object' && 'reason' in error ? ':' + String((error as { reason: unknown }).reason) : '')); }
  if (packAllyInReach) return unavailable('reaction-pack-tactics-possible');
  if (sight !== 'visible') return unavailable('reaction-target-' + (sight ?? 'visibility-unknown'));
  // Resolve the possible terminal prefix before drawing. The same envelope then stops a killed/Dying mover at the strike.
  let halted: PlainWalkResult = { ...walk, updates: { x_pos: Number(ftPosCoord(target, 'x_pos') ?? walk.origin.x),
    y_pos: Number(ftPosCoord(target, 'y_pos') ?? walk.origin.y), z_pos: walk.origin.z, ...walk.before },
    zone_entries: undefined, follower_zone_entries: undefined, zone_exits: undefined,
    route: [[walk.origin.x, walk.origin.y, walk.origin.z], [walk.origin.x, walk.origin.y, walk.origin.z]] };
  halted.destination = { x: halted.updates.x_pos, y: halted.updates.y_pos, z: halted.updates.z_pos };
  if (!forced && leaveIndex > 0) {
    const destination = { x: Number(ftPosCoord(target, 'x_pos')), y: Number(ftPosCoord(target, 'y_pos')), z: walk.origin.z };
    const prefix = resolvePlainWalkScope({ ...input, destination, route: cutRouteAtFootIndex(walk.route, leaveIndex) }, encounter, mover, moveRead, committed);
    if (!prefix.supported) return unavailable('reaction-halt-prefix-not-held');
    halted = withUnpricedBank(prefix.walk);
  }
  if (sequence) { walk = sequence.price(walk); halted = sequence.price(halted, leaveIndex); }
  const deathResources = lazyDeathResources(encounter.encounter_creatures_arr as Row[] ?? [], encounter,
    abilityTruthReaders({}, encounter, new Set()), campaignFor(encounter));
  const attackInput: WeaponAttackCommitInput = { encounterId: input.encounterId, actorId: upper(watcherId), targetId: input.actorId, weaponId, opportunityAttack: true, triggerConsent: consent, deathResources,
    ...(elevationStacks !== 0 ? { elevationStacks } : {}), ...(flankingSource ? { flankingSource } : {}) };
  // Keep the strike geometry, but let command preparation bind the selected inventory
  // weapon and its skill/passives through the same readers as an ordinary attack.
  const geometry = { opposingTeams: read.opposingTeams, distanceFt: read.distanceFt };
  const walkTargets = new Set([upper(input.actorId), ...(walk.compound?.members ?? []).map(member => upper(member.id))]);
  const checkEffects: NonNullable<Parameters<typeof resolveWeaponAttackWithReactions>[7]> = effects => {
    const removedZoneSources = new Set<string>();
    for (const removed of effects.statusesRemoved) {
      const holder = actorRowFor(input.encounterId, removed.target);
      const stored = holder && (ftCreatureStatuses(holder, 'target') as Row[]).find(status => upper(statusScalar(status, 'status_id')) === upper(removed.status_id));
      if (stored && num(statusScalar(stored, 'status_aoe_source')) === 1) removedZoneSources.add(upper(removed.status_id));
    }
    const movingActor = upper(input.actorId);
    const terminal = effects.ec[movingActor]?.encounter_creature_dead === 1
      || effects.statusesAdded.some(status => upper(status.target) === movingActor && status.status_item_id === '8AE129AF-52D8-CCE8-706A-85767DB52740')
      || effects.spawnRows?.writes.some(write => write.table === 'encounter_creatures' && write.row.encounter_creature_id === movingActor
        && write.row.encounter_creature_deleted === 1) === true;
    // Actual terminal posture stops at the strike; its own speed/action locks never price later feet.
    const changes = [...effects.statusesAdded, ...effects.statusesRemoved, ...effects.statusStacks]
      .filter(change => !terminal || upper(change.target) !== movingActor || removedZoneSources.has(upper(change.status_id)));
    return damageMovementStatusFailure(changes, {
      definition: id => definitionFor(mover, encounter, id, true), entries: ftLayerEntries }, { targets: walkTargets, removedZoneSources });
  };
  // The shared swing preflights held damage statuses before drawing; death is settled only on its actual result.
  if (walk.compound?.members.length && (walk.zone_entries?.length || walk.follower_zone_entries?.length || walk.zone_exits?.length))
    return unavailable('reaction-compound-zone-work');
  // An intermediate OA saves only the paid prefix up to its strike. Later watchers
  // resolve against that installed vitality, statuses, movement bank and revision.
  const continuingRoute = sequence?.more ? path.slice(leaveIndex).map(p => [p[0]!, p[1]!] as [number, number]) : undefined;
  if (sequence?.more) walk = halted;
  // Entries before the strike, the swing, then only the reached suffix and exit sweep share one frame.
  // The envelope moves the mover alone; compound zone work still requires its own contract.
  stage.committing = true;
  const hasZoneWork = !forced && [walk, halted].some(w => w.zone_entries?.length || w.follower_zone_entries?.length || w.zone_exits?.length);
  const work = hasZoneWork ? opportunityWalkZoneWork(input.actorId, walk, halted, leaveIndex,
    opportunityZoneEntryRecords(encounter, walk, halted),
    (id, zoneId) => creatureStandsInZone(input.encounterId, id, zoneId), step => {
      const route = cutRouteAtFootIndex(walk.route, step), point = route[route.length - 1]!;
      const prefix = resolvePlainWalkScope({ ...input, route, destination: { x: point[0]!, y: point[1]!, z: point[2] ?? walk.origin.z } },
        encounter, mover, moveRead, committed);
      if (!prefix.supported) throw new HandBack('reaction-zone-halt-prefix-not-held');
      return withUnpricedBank(prefix.walk);
    }) : undefined;
  // From here on a draw belongs to this lane. No legacy fallback after preparation or enqueue.
  let command: ReturnType<typeof prepareWeaponAttackCommand>;
  const rolls = resumableRollSource(browserRollSource());
  let frozen: { encounter: Row; attacker: MovementTruthRow; target: MovementTruthRow } | undefined;
  const freeze = (): void => { frozen ??= frozenAttackFrame(encounter, attacker, target); };
  if (work) {
    let pending = false;
    command = yield* awaitWalkPlan(planWithConsent(input.encounterId, input.actorId, questions => resumeDeathResources(() => {
      const answers: TriggerConsent = { get final() { return questions.final; },
        controller: reactor => questions.controller(reactor), need: request => questions.need(request), pending: () => questions.pending(),
        answer: (id, ability) => consent?.answer(id, ability) ?? questions.answer(id, ability) };
      const planned = prepareWeaponAttackCommand({ ...attackInput, triggerConsent: answers, rollSource: rolls.next() },
        { ...frozen ?? { encounter, attacker, target }, geometry, checkEffects, work });
      pending = questions.pending().length > 0;
      if (pending && rolls.draws.length) freeze();
      return planned;
    }, freeze, deathResources), planned => planned === null && pending));
  } else {
    const plan = () => prepareWeaponAttackCommand({ ...attackInput, rollSource: rolls.next() },
      { ...frozen ?? { encounter, attacker, target }, geometry, checkEffects });
    try { command = plan(); }
    catch (error) {
      if (!deathResourcesPending(error)) throw error;
      freeze();
      yield error.loading;
      command = yield* awaitWalkPlan(resumeDeathResources(plan, freeze, deathResources));
    }
  }
  if (!command) return unavailable('reaction-prepare-stepped-aside');
  if (command === 'refused') throw new MovementRefused('The opportunity reaction could not be prepared. Try moving again.');
  walk = work?.stoppedWalk() ?? (command.frame.outcome.death !== null ? halted : walk);
  if (sequence?.more) {
    const destination = { x: Number(walk.updates.x_pos.toFixed(3)), y: Number(walk.updates.y_pos.toFixed(3)), z: Number(walk.updates.z_pos.toFixed(3)) };
    walk = { ...walk, destination, updates: { ...walk.updates, x_pos: destination.x, y_pos: destination.y, z_pos: destination.z },
      route: walk.route.map((p, index) => index === walk.route.length - 1 ? [destination.x, destination.y, destination.z] : p) };
  }
  if (sequence?.more && command.frame.outcome.death === null && !work?.stoppedWalk() && continuingRoute) {
    sequence.next = { ...input, origin: { x: walk.updates.x_pos, y: walk.updates.y_pos, z: walk.updates.z_pos }, route: continuingRoute };
    sequence.steps = leaveIndex;
  }
  command.movement = { before: { x_pos: walk.origin.x, y_pos: walk.origin.y, z_pos: walk.origin.z, ...walk.before }, updates: { ...walk.updates }, route: walk.route };
  command.frame.target.movement = command.movement.updates;
  const envelope: OpportunityWalkCommand = { schema: OPPORTUNITY_WALK_SCHEMA, operation_id: command.operation_id,
    encounter_id: input.encounterId, actor_id: input.actorId, expected_rev: command.target_expected_rev, before: command.movement.before, reaction: command };
  stageAppliedEntities(envelope.operation_id, attackEffects(command).map(effect => effect.spawnRows));
  try {
    const applied = actorOutbox(input.encounterId, input.actorId).enqueue(withLogParticipants(envelope));
    if (sequence) sequence.saved = applied.commit;
  }
  catch (error) { forgetRejectedAttack(command as unknown as ActorTruthCommandBase); throw new MovementRefused(`Move and reaction could not be queued: ${String(error)}`); }
  noteOwnOperation(command.operation_id, OPPORTUNITY_WALK_SCHEMA); seen.add(command.operation_id);
  // The opportunity swing presents at send too (#8c): its strike, flinch and hit/whiff take share one impact.
  const impactAt = attackImpactAt(command.operation_id, command.actor_id);
  if (!command.swing_skipped) {
    presentAttackAtSend(command.operation_id, command.actor_id, command.target_id, command.frame.outcome.hit === true, impactAt);
    void playActionSound(command.operation_id, (command as unknown as Row).sound, { hitAt: impactAt });
  }
  return walk;
}

/** CROSS-LANE SEAM (lane A11 → MV3 / MV4; OWNER RULING 2026-10-02 — optional triggers are prompt-then-resolve, 5 s, silence = decline,
 *  player-controlled creatures in campaign encounters only): a walk input may carry a PRE-ANSWERED consent reader (a PageTriggerConsent the
 *  walk page filled with askTriggerConsent BEFORE it resolves — the movement reaction resolver above is synchronous and never asks itself). */
export type WalkReactionConsentInput = PlainWalkCommitInput & { triggerConsent?: TriggerConsent };
const isOptionalReactionDefinition = (definition: Row): boolean =>
  ['reaction_ability', 'opportunity_attack_ability'].includes(String(ftItemsCol(definition, 'item_type'))) || String(ftLayerValue(definition, 'spell_casting_time') ?? '') === 'reaction';
/** The questions a walk's opportunity reaction raises, for the walk page to ask FIRST (then pass the reader as `input.triggerConsent`): the
 *  WATCHER's opportunity attack, and the MOVER's own optional damage-taken reactions that swing could set off. A creature no player controls
 *  (controller null — an NPC / a GM's creature) is never asked (it auto-resolves); '?' (the page cannot name the controller) is asked through
 *  the relay, which decides. Empty = nothing to ask. */
export function opportunityConsentQuestions(encounterId: string, mover: Row, watcherId: string): TriggerConsentRequest[] {
  const out: TriggerConsentRequest[] = [];
  const watcher = actorRowFor(upper(encounterId), upper(watcherId)) as Row | null;
  const encounter = actorEncounterFor(upper(encounterId)) as Row | null;
  const ask = (row: Row, abilityId: string, abilityName: string, trigger: string): void => {
    const inciting = row === watcher ? mover : watcher;
    if (!passesDbGate({ value: ftLayerValue }, abilityId, row,
      inciting ? String(ftEncounterCreatureField(inciting.encounter_creatures, 'encounter_creature_id') ?? '') : null)) return;
    const controllerUserId = triggerController(upper(encounterId), row);
    const reactorId = upper(asRow(row.encounter_creatures)?.encounter_creature_id);
    if (controllerUserId && reactorId) out.push({ key: consentKey(reactorId, abilityId), reactorId, abilityId: upper(abilityId), abilityName, trigger, controllerUserId });
  };
  if (watcher) ask(watcher, OPPORTUNITY_ATTACK_ID, 'Opportunity Attack', 'triggers_when_target_leaves_reach');
  const seen = new Set<string>();
  for (const entry of [...carrierEntries(mover), ...ftCreatureStatuses(mover, 'target') as Row[]]) {
    const definition = carrierDefinition(mover, encounter, entry);
    if (!definition || !isOptionalReactionDefinition(definition)) continue;
    const trigger = ['triggers_when_caster_takes_any_damage', 'triggers_when_caster_takes_damage_type']
      .find(key => ftLayerEntries(definition, key).length > 0);
    if (!trigger) continue;
    const id = upper(ftItemsCol(definition, 'item_id'));
    if (!id || seen.has(id) || [EVADE_ID, FORTUNATE_ID].includes(id)) continue;
    seen.add(id);
    ask(mover, id, String(ftItemsCol(definition, 'item_name') ?? ''), trigger);
  }
  return out;
}

export interface WeaponAttackCommitInput {
  deathResources?: JanusCtx['read']['deathResources'];
  /** The same actual draw prefix survives an outcome-triggered consent prompt. */
  rollSource?: RollSource;
  encounterId: string; actorId: string; targetId: string; weaponId: string;
  /** Attached attack id; weaponId remains the exact inventory instance. */
  abilityId?: string;
  attackMode?: 'throw_weapon' | null;
  rawDiceMod?: number; rawDiceAdvDisStacks?: number; rollPrivate?: number;
  opportunityAttack?: boolean;
  /** D-012: the OA swing's elevation stack, decided pre-draw by the movement-reaction resolver. */
  elevationStacks?: number;
  /** Shared attack-time flanking verdict at the OA leave point. */
  flankingSource?: NonNullable<ReturnType<typeof weaponAttackFlankingSource>>;
  /** OWNER RULING 2026-10-02 (lane A11): the TARGET's controller's answer about its pre-roll Evade (asked by tryCommitWeaponAttack when the
   *  resolver needed it; absent = not asked yet). */
  evadeAnswer?: boolean;
  evadeAnswers?: Record<string, boolean>;
  /** Pre-draw planning signals: Evade needs consent, or an unbound natural weapon needs the shared ability lane. */
  consentProbe?: { evade: boolean; targets?: string[]; naturalWeapon?: string };
  triggerConsent?: TriggerConsent;
}
/** Null suspends consent planning; every unavailable input names its refusal here.
 * Successful commands use the shared outbox. */
export function prepareWeaponAttackCommand(input: WeaponAttackCommitInput, reaction?: {
  encounter: Row; attacker: MovementTruthRow; target: MovementTruthRow; read?: WeaponAttackReaders; frozen?: true;
  geometry?: Pick<WeaponAttackReaders, 'opposingTeams' | 'distanceFt'>;
  preview?: boolean; checkEffects?: Parameters<typeof resolveWeaponAttackWithReactions>[7]; work?: Parameters<typeof resolveWeaponAttackWithReactions>[8];
}): WeaponAttackCommand | 'refused' | null {
  const encounterId = upper(input.encounterId), actorId = upper(input.actorId), weaponId = upper(input.weaponId);
  const triggered = !!reaction && !reaction.frozen;
  const selectedTargets = input.targetId.split(',').map(upper).filter(Boolean);
  let targetId = selectedTargets[0] ?? '';
  const heldEncounter = reaction?.encounter ?? actorEncounterFor(encounterId);
  let attacker = reaction?.attacker ?? actorRowFor(encounterId, actorId);
  const savedMembers = heldEncounter ? savedAttackMembers(heldEncounter) : new Map<string, Row>();
  const encounter = heldEncounter ? { ...heldEncounter, encounter_creatures_arr: [...savedMembers.values()] } : null;
  let target = reaction?.target ?? actorRowFor(encounterId, targetId);
  if (heldEncounter && encounter) retainHeldBase(heldEncounter, encounter);
  const fortunateInstalled = !!attacker && (ftCreatureStatuses(attacker, 'target') as Row[]).some(isFortunateStatus);
  if (fortunatePending.waiting(fortunateActorKey(encounterId, actorId), fortunateInstalled)) {
    window.dispatchEvent(new CustomEvent('ft:attack-fortunate-pending', { detail: { encounterId, actorId } }));
    if (typeof displayWarning === 'function') displayWarning('Fortunate is still being applied. Wait for the attack to finish.');
    return 'refused'; // No draws, no queued second attack and no early legacy fallback.
  }
  const unavailable = (reason: string): 'refused' => {
    refuseUnported(reason.startsWith('weapon:') ? reason : 'weapon:' + reason, { abilityId: input.abilityId, weaponId });
    return 'refused';
  };
  if (!encounter) return unavailable('encounter-unavailable');
  if (!attacker) return unavailable('attacker-unavailable');
  if (!target) return unavailable('target-unavailable');
  const ownerId = upper(ftEncounterCreatureField(attacker.encounter_creatures, 'encounter_creature_created_by_id'));
  if (!triggered && ownerId !== upper(window.FateTide?.user?.id) && window.FateTide?.user?.isGM !== true) return unavailable('actor-not-authorized');
  // The asynchronous entry holds the selected definitions before this synchronous plan.
  let bound: { weapon: Row; ability: Row; skill: string; inventoryId: string; natural?: boolean } | undefined;
  let throwingWeapon = false;
  const projectilePlans = new Map<string, { finish: (hit: boolean, roll: import('../janus-engine/actions/roll-source.js').RollSource) => WeaponInventoryChange }>();
  const riderProjectilePlans: typeof projectilePlans = new Map();
  const inventoryWeapon = inventoryWeaponSelected(attacker as Row, weaponId);
  if (!savedMembers.has(actorId) || !savedMembers.has(targetId)) return null;
  if (input.abilityId || inventoryWeapon) {
    if (inventoryWeapon && typeof (attacker as Row)._inventory_refusal === 'string') {
      reportWeaponAttackRefusal('weapon:inventory-instance-unavailable', { abilityId: input.abilityId, weaponId }); return 'refused';
    }
    const ability = input.abilityId ? definitionFor(attacker as Row, encounter, upper(input.abilityId)) : plainWeaponAbility(weaponId);
    if (!ability) { refuseUnported('weapon:attack-definition-unavailable', { abilityId: input.abilityId, weaponId }); return 'refused'; }
    const bindingRead = explicitWeaponReaders(attacker as Row, encounter);
    const throwing = input.attackMode === 'throw_weapon' || ftLayerEntries(ability, 'attack_ability_throws_the_throwing_weapon_at_target').length > 0;
    const natural = !inventoryWeapon && String(ftItemsCol(definitionFor(attacker as Row, encounter, weaponId), 'item_type')) === 'natural_weapon';
    if (natural && throwing) { refuseUnported('weapon:natural-cannot-be-thrown', { weaponId }); return 'refused'; }
    const binding = natural ? bindNaturalWeaponAttack(ability, attacker as Row, weaponId, bindingRead)
      : bindExplicitWeaponAttack(ability, attacker as Row, weaponId, bindingRead, throwing);
    const reason = 'reason' in binding ? binding.reason : explicitWeaponFamilyRefusal(binding, attacker as Row, bindingRead, throwing);
    if (reason) { reportWeaponAttackRefusal(reason, { abilityId: input.abilityId, weaponId }); return 'refused'; }
    if ('reason' in binding) return 'refused';
    if (skillEntry(attacker as Row, binding.skill)?.forced_natural_main_roll != null) {
      refuseUnported('weapon:skill-forced-natural-unavailable', { weaponId }); return 'refused';
    }
    throwingWeapon = throwing;
    try {
      const possibleTargets = [target as Row, ...(weaponAttackRollType({ throwWeapon: throwing }, binding.weapon, bindingRead) === '3F85CA97-F9B2-A83A-FDE8-E523FD99346C'
        ? weaponStrayCandidates(encounter, attacker as Row, target as Row) : [])];
      const ammo = !natural && !throwing && ftLayerEntries(binding.weapon, 'uses_ammo_classification').length ? findWeaponAmmo(attacker as Row, binding.weapon, bindingRead) : null;
      if (!natural && !throwing && ftLayerEntries(binding.weapon, 'uses_ammo_classification').length && !ammo) {
        refuseUnported('weapon:ranged-ammo-unavailable', { weaponId }); return 'refused';
      }
      for (const candidate of possibleTargets) {
        const id = upper(ftEncounterCreatureField(candidate.encounter_creatures, 'encounter_creature_id'));
        const statusIds = (row: Row): string[] => (ftCreatureStatuses(row, 'target') as Row[]).map(st => String(scalar(st.status_item_id) ?? ''));
        const plan = throwing ? planThrownWeapon(binding.weapon, candidate, encounter, actorId, encounterId, bindingRead, ftPosCoord, statusIds, () => crypto.randomUUID().toUpperCase())
          : ammo ? planWeaponAmmo(ammo, candidate, encounter, encounterId, id, bindingRead, ftPosCoord, () => crypto.randomUUID().toUpperCase(), statusIds) : null;
        if (plan && 'reason' in plan) {
          refuseUnported(throwing ? plan.reason : plan.reason.replace(/^weapon:/, 'weapon:ranged-'), { weaponId });
          return 'refused';
        }
        if (plan && !throwing && candidate === possibleTargets[0] && 'weapon' in plan && typeof plan.weapon === 'function')
          binding.weapon = plan.weapon(binding.weapon) as Row;
        if (plan) projectilePlans.set(id, plan);
      }
    } catch (error) { refuseUnported('weapon:ranged-' + String(error instanceof Error ? error.message : error), { weaponId }); return 'refused'; }
    try {
      if (!explicitWeaponSizeAllowed(binding.ability, attacker as Row, target as Row, bindingRead)) {
        refuseUnported('weapon:size-restriction', { abilityId: input.abilityId, weaponId }); return 'refused';
      }
      explicitWeaponChargeContext(binding.ability, attacker as Row, encounter, bindingRead,
        { d: () => { throw new Error('weapon:charge-preflight-draw'); }, fraction: () => { throw new Error('weapon:charge-preflight-draw'); } },
        creatureIsInActiveCombat(encounter, attacker));
    } catch (error) { refuseUnported(String(error instanceof Error ? error.message : error), { abilityId: input.abilityId, weaponId }); return 'refused'; }
    bound = { ...binding, inventoryId: weaponId, natural };
  }
  let approach: { start: { x: number; y: number; z: number }; end: { x: number; y: number; z: number } } | undefined;
  if (bound && hasMoveTowardsTarget({ read: { has: (row, key) => ftLayerEntries(row, key).length > 0 } }, bound.ability)) {
    try {
      const moveRead = abilityTruthReaders(attacker as Row, encounter, new Set([actorId, targetId]));
      const noDraw = (): never => { throw new Error('weapon:move-toward:unexpected-draw'); };
      const ctx = attackDamageFrame(encounter, savedMembers, moveRead, { d: noDraw, fraction: noDraw }, 0, frameCopyOnRead(encounter, savedMembers));
      const plan = planCasterTowardsTarget(ctx, bound.ability, ctx.members.get(actorId)!, [ctx.members.get(targetId)!], selectedTargets.length);
      if (plan.kind === 'move') {
        approach = plan;
        const axes = { x_pos: plan.end.x, y_pos: plan.end.y, z_pos: plan.end.z };
        attacker = { ...attacker, position: { ...asRow(attacker.position), ...axes },
          encounter_creatures: { ...attacker.encounter_creatures, ...axes } };
        encounter.encounter_creatures_arr = (encounter.encounter_creatures_arr as Row[]).map(row =>
          upper(ftEncounterCreatureField(row.encounter_creatures, 'encounter_creature_id')) === actorId ? attacker as Row : row);
      }
    } catch (error) {
      return unavailable('move-toward:' + (error instanceof HandBack ? error.reason.replace(/^move_caster_moves_towards_target_in_ft:(walk-toward:)?/, '')
        : String(error instanceof Error ? error.message : error)));
    }
  }
  if (selectedTargets.length > 1) return unavailable('target-count');
  const weapon = bound?.weapon ?? definitionFor(attacker as Row, encounter, weaponId);
  if (!weapon) return unavailable('weapon-definition-unavailable');
  if (!bound && input.attackMode === 'throw_weapon') {
    refuseUnported('weapon:natural-cannot-be-thrown', { weaponId }); return 'refused';
  }
  const unboundNatural = !bound && ftItemsCol(weapon, 'item_type') === 'natural_weapon';
  const naturalStepAside = (reason: string): null => {
    if (input.consentProbe) input.consentProbe.naturalWeapon = reason;
    window.dispatchEvent(new CustomEvent('ft:action-slice-unavailable', { detail: { reason, schema: WEAPON_ATTACK_SCHEMA, weaponId } }));
    return null;
  };
  const onHit = triggered || input.opportunityAttack ? null : k4OnHitStepAside(encounter, attacker as Row, target as Row, weapon,
    abilityTruthReaders(attacker as Row, encounter, new Set([actorId, targetId])));
  // Residual reaction/on-hit families are checked before draws. Spatial rules resolve in A2.
  {
    const aside = onHit
      ?? (!triggered && a2SleepingTarget(encounter, target as Row) ? 'sleeping-target' : null)
      ?? (!triggered && a2ContestRetryTarget(encounter, target as Row) ? 'contest-retry-target' : null);
    if (aside) {
      if (unboundNatural) return naturalStepAside(aside);
      if (bound) {
        const reason = throwingWeapon ? aside : aside.replace('weapon:throw-', 'weapon:ranged-');
        refuseUnported(reason.startsWith('weapon:') ? reason : 'weapon:family:' + reason, { abilityId: input.abilityId, weaponId });
        return 'refused';
      }
      return unavailable(aside);
    }
  }
  // Both revisions are confirmed + pending (every legacy install banked itself).
  confirmedRevFor(encounterId, actorId, attacker);
  confirmedRevFor(encounterId, targetId, target);
  let read: WeaponAttackReaders;
  let scope;
  const roll = recordingRollSource(input.rollSource ?? (reaction?.preview ? syntheticRollSource('max') : browserRollSource()));
  const drawn = (): boolean => !reaction?.preview && roll.draws.length > 0;
  const abilityRead = abilityTruthReaders(attacker as Row, encounter, new Set([actorId, targetId]));
  const passiveRead = bound ? explicitWeaponAttackReaders(bound.weapon, attacker as Row,
    { ...explicitWeaponReaders(attacker as Row, encounter), value: abilityRead.value, entries: abilityRead.entries }) : null;
  const statusRead = { ...abilityRead,
    ...(passiveRead ? { value: passiveRead.value, entries: passiveRead.entries } : {}),
    ...(input.deathResources ? { deathResources: input.deathResources } : {}),
    ...(input.triggerConsent ? { triggerConsent: input.triggerConsent } : {}) };
  const complete = statusRead.reactionInventoryComplete;
  statusRead.reactionInventoryComplete = row => complete(row) || statusRead.bystanderReactionInventoryHeld?.(row) === true;
  const heldDefinition = statusRead.definition;
  statusRead.definition = id => heldDefinition(id) ?? definitionFor(target as Row, encounter, id, true);
  const statusPlan = (hits: number, fortunate: boolean): Extract<AbilityUseVerdict, { kind: 'resolved' }> => {
    const members = new Map<string, Row>();
    for (const raw of Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) {
      const member = asRow(raw); if (member) members.set(upper(ftEncounterCreatureField(member.encounter_creatures, 'encounter_creature_id')), member);
    }
    if (fortunate) members.set(actorId, { ...attacker,
      // This grant follows the swing's consumption, including in the no-draw preflight.
      statuses: { ...asRow(attacker.statuses), target: (ftCreatureStatuses(attacker, 'target') as Row[]).filter(status => !isFortunateStatus(status)) },
      encounter_creatures: { ...attacker.encounter_creatures,
      luck: Math.min(essenceTotal(attacker as Row, 'fortune'), Math.max(0, num(ftEncounterCreatureField(attacker.encounter_creatures, 'luck'))) + 1) } });
    const result = resolveAttackStatusEffects({ actorId, targetId, shieldedHits: hits, fortunate, rollPrivate: input.rollPrivate }, encounter, members,
      statusRead, { d: () => { throw new AttackInputUnavailable('attack:status-trigger-roll-out-of-scope'); },
        fraction: () => { throw new AttackInputUnavailable('attack:status-trigger-roll-out-of-scope'); } });
    if (result.kind !== 'resolved') throw new AttackInputUnavailable('attack:status-effects:' + ('reason' in result ? result.reason : result.message));
    const unsupportedLog = result.rows.find(r => !['no_roll', 'reaction_ability_applied', 'passive_ability_applied', 'status_stack_decreased', 'janus_unported_skip'].includes(r.roll_type_1));
    if (unsupportedLog) throw new AttackInputUnavailable('attack:status-trigger-log-out-of-scope:' + unsupportedLog.roll_type_1);
    // These status tails run before the damage application. Economy-changing/rolled listeners need a whole-swing executor.
    if (Object.entries(result.ec).some(([id, changes]) => id !== actorId || Object.keys(changes).some(k => !['luck', 'reaction'].includes(k)))
        || result.charges.length || result.chargesRemoved?.length || result.playerCharactersChanged.length || result.inventory.length || result.deathTail.length || result.concentrationBreaks.length || result.statusTriggerListeners?.length)
      throw new AttackInputUnavailable('attack:status-trigger-economy-out-of-scope');
    if (fortunate && (result.statusesAdded.some(s => upper(s.status_item_id) !== FORTUNATE_ID)
        || result.statusesRemoved.length || result.statusStacks.length))
      throw new AttackInputUnavailable('attack:status-trigger-payload-out-of-scope');
    if (fortunate && (num(result.ec[actorId]?.luck) !== num(ftEncounterCreatureField(members.get(actorId)!.encounter_creatures, 'luck')) - 1
        || num(result.ec[actorId]?.reaction, 1) !== 0)) throw new AttackInputUnavailable('attack:status-trigger-economy-out-of-scope');
    for (const member of members.values()) {
      const reason = attackStatusPlanFailure(member, result, id => statusProjectionDefinition(member, encounter, id), members, (m, current) => possessorSceneContext(encounter, m, current));
      if (reason) throw new AttackInputUnavailable(reason);
    }
    return result;
  };
  try {
    const projectionGap = statusProjectionGaps.get(encounterId);
    if (projectionGap) { recoverStatusProjection(encounterId, projectionGap); throw new AttackInputUnavailable(projectionGap); }
    read = reaction?.read ?? readers(attacker as Row, encounter, bound);
    if (bound && !throwingWeapon && ftLayerEntries(bound.weapon, 'uses_ammo_classification').length) {
      const definition = read.definition;
      read = { ...read, definition: id => upper(id) === upper(bound.inventoryId) ? bound.weapon : definition(id) };
    }
    if (reaction?.geometry) read = { ...read, ...reaction.geometry };
    read = weaponPartnerReaders(read, attacker as Row, encounter, riderProjectilePlans, projectilePlans.size > 0);
    const canFortunate = !input.opportunityAttack && (!reaction || reaction.frozen) && (read.fortunateOnGain?.(attacker as Row) ?? false);
    if (canFortunate) statusPlan(0, true);
    if (shieldedChanges(target as Row).length) statusPlan(1, false);
    // OWNER RULING 2026-10-02 (lane A11): a click's target Evade — a creature no player controls auto-resolves; a player's waits for its
    // controller's answer (tryCommitWeaponAttack asks, ≤ 5 s). An opportunity attack suppresses Evade itself (no reader needed).
    if (!reaction || reaction.frozen) read = { ...read, evadeConsent: (t: Row) => {
      if (!triggerController(encounterId, t)) return true;
      const id = upper(ftEncounterCreatureField(t.encounter_creatures, 'encounter_creature_id'));
      const answer = input.evadeAnswers?.[id] ?? (id === targetId ? input.evadeAnswer : undefined);
      if (answer === undefined && input.consentProbe) {
        input.consentProbe.evade = true;
        const targets = input.consentProbe.targets ??= [];
        if (!targets.includes(id)) targets.push(id);
      }
      return answer;
    } };
    read = { ...read, strayTargetPreflight: candidate =>
      (a2SleepingTarget(encounter, candidate) ? 'sleeping-target' : null)
      ?? (a2ContestRetryTarget(encounter, candidate) ? 'contest-retry-target' : null)
      ?? (!bound || explicitWeaponSizeAllowed(bound.ability, attacker as Row, candidate, { value: ftLayerValue, entries: ftLayerEntries, inventoryField: ftInventoryField, itemField: ftItemsCol, computedValue: ftComputedValue, itemType: row => String(ftItemsCol(row, 'item_type') ?? '') }) ? null : 'size-restriction') };
    scope = resolveWeaponAttackWithReactions({ encounterId, actorId, targetId, weaponId, explicitInventoryWeapon: !!bound && !bound.natural, throwWeapon: throwingWeapon, opportunityAttack: input.opportunityAttack, suppressPartner: triggered, rawDiceMod: input.rawDiceMod ?? 0, rawDiceAdvDisStacks: input.rawDiceAdvDisStacks ?? 0, rollPrivate: input.rollPrivate ?? 0,
      ...(reaction && input.elevationStacks ? { elevationStacks: input.elevationStacks } : {}),
      ...(reaction && input.flankingSource ? { flankingSource: input.flankingSource } : {}) },
      encounter, attacker as Row, target as Row, read, statusRead, roll, reaction?.checkEffects, reaction?.work, bound?.ability);
    const source = input.rollSource as (RollSource & { remaining?: () => number }) | undefined;
    if (scope.supported && (source?.remaining?.() ?? 0) !== 0) throw new Error('The attack changed while its reaction prompt was open.');
  } catch (error) {
    // A drawn attack stays owned by this lane, including natural weapon failures.
    if (deathResourcesPending(error)) throw error;
    if (unboundNatural && !drawn()) return naturalStepAside(String(error instanceof Error ? error.message : error));
    return unavailable(String(error instanceof Error ? error.message : error));
  }
  if (!scope.supported) {
    if (!scope.refused && scope.reason.includes(`outcome:${TRIGGER_CONSENT_REASON}`) && input.triggerConsent && !input.triggerConsent.final) return null;
    if (!roll.draws.length && !scope.refused && scope.reason.includes(TRIGGER_CONSENT_REASON) && input.triggerConsent?.pending().length) return null;
    if (scope.refused) { if (typeof displayWarning === 'function') displayWarning(scope.message); return 'refused'; }
    if (drawn()) {
      window.dispatchEvent(new CustomEvent('ft:action-slice-unavailable', { detail: { reason: scope.reason, schema: WEAPON_ATTACK_SCHEMA, weaponId, postDraw: true } }));
      return unavailable(scope.reason);
    }
    if (scope.reason === `preflight:${TRIGGER_CONSENT_REASON}` && input.consentProbe && !input.triggerConsent?.final) { input.consentProbe.evade = true; return null; }
    if (unboundNatural) return naturalStepAside(scope.reason);
    return unavailable(scope.reason);
  }
  const intendedTarget = target;
  if (scope.outcome.stray) {
    targetId = scope.outcome.stray.targetId;
    target = (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : [])
      .find((row: Row) => upper(ftEncounterCreatureField(row.encounter_creatures, 'encounter_creature_id')) === targetId) as typeof target;
    if (!target) throw new Error('Preflighted stray target disappeared');
    confirmedRevFor(encounterId, targetId, target);
  }
  const aec = attacker.encounter_creatures as Row, tec = target.encounter_creatures as Row;
  const damageTypeId = read.damageTypeId(weapon);
  const damageTypes = (window as unknown as { DAMAGETYPE_CATALOG?: Record<string, Row> }).DAMAGETYPE_CATALOG;
  const damageTypeNameOf = (id: string): string => String(damageTypes?.[id]?.item_name ?? damageTypes?.[id.toLowerCase()]?.item_name ?? 'Unknown');
  const riderOutcome = scope.outcome.rider;
  const riderWeapon = riderOutcome ? read.definition(riderOutcome.weaponId) : null;
  const riderSkillId = riderWeapon ? skillIdForVariable(weaponAttackSkill(riderWeapon, attacker as Row, explicitWeaponReaders(attacker as Row, encounter))) : NATURAL_WEAPON_SKILL;
  if (riderOutcome && !riderWeapon) {
    // Unreachable by construction (the resolver read this very definition before drawing), and the draws are already
    // taken — so this is a REFUSAL, never a legacy fallback (plan §12 invariant). Nothing was applied or queued.
    if (typeof displayWarning === 'function') displayWarning('Attack could not be committed: the off-hand weapon definition is unavailable.');
    window.dispatchEvent(new CustomEvent('ft:attack-truth-refused', { detail: { command: null, info: { error: 'rider-definition-missing', status: 0, terminal: true } } }));
    return 'refused';
  }
  const ammoChange = projectilePlans.get(targetId)?.finish(scope.outcome.hit, roll)
    ?? (riderOutcome ? riderProjectilePlans.get(targetId)?.finish(riderOutcome.hit, roll) : undefined);
  const commandInput: Parameters<typeof buildWeaponAttackCommand>[3] = {
    operationId: crypto.randomUUID().toUpperCase(), encounterId, actorId, targetId, weaponId, abilityId: input.abilityId,
    attackRollType: weaponAttackRollType({ throwWeapon: throwingWeapon }, weapon, read),
    rollPrivate: input.rollPrivate ?? 0, rawDiceMod: input.rawDiceMod ?? 0, rawDiceAdvDisStacks: input.rawDiceAdvDisStacks ?? 0,
    attackerRev: num(aec.movement_rev), targetRev: num(tec.movement_rev),
    attackerBefore: { aps: num(aec.aps), aps_total: num(aec.aps_total, 3), luck: num(aec.luck), reaction: num(aec.reaction), head_attack: num(aec.head_attack), weapon_attack: num(aec.weapon_attack) },
    targetBefore: { v_current: num(tec.v_current), ...(scope.deltas.target.v_temp !== undefined ? { v_temp: num(ftEncounterCreatureField(target.encounter_creatures, 'v_temp')) } : {}),
      reaction: num(tec.reaction), encounter_creature_dead: num(tec.encounter_creature_dead), luck: num(tec.luck) },
    weapon: { item_id: weaponId, item_name: String(ftItemsCol(weapon, 'item_name') ?? ''), item_type: String(ftItemsCol(weapon, 'item_type') ?? ''), item_composition: ftLayerValue(weapon, 'item_composition') },
    damageTypeId, damageTypeName: damageTypeNameOf(damageTypeId), damageTypeNameOf,
    essenceName: 'body', essenceBonus: read.essenceBonus(attacker as Row, weapon), damageEssenceBonus: read.damageEssenceBonus(attacker as Row, weapon),
    ...(riderOutcome && riderWeapon ? { rider: {
      weapon: { item_id: upper(riderOutcome.weaponId), item_name: String(ftItemsCol(riderWeapon, 'item_name') ?? ''), item_type: String(ftItemsCol(riderWeapon, 'item_type') ?? ''), item_composition: ftLayerValue(riderWeapon, 'item_composition') },
      skillId: riderSkillId,
      skillName: String((window as unknown as { SKILL_CATALOG?: Record<string, Row> }).SKILL_CATALOG?.[riderSkillId]?.item_name ?? 'Weapon Skill'),
      damageTypeId: riderOutcome.damageTypeId, damageTypeName: damageTypeNameOf(riderOutcome.damageTypeId), weaponDice: riderOutcome.dice, weaponDie: riderOutcome.die,
    } } : {}),
    // The skill named in an `untrained` trace entry (skill_roll_inc.php: origin_name = the skill's item_name, origin_item_id = its id).
    skillId: bound ? skillIdForVariable(bound.skill) : NATURAL_WEAPON_SKILL,
    skillName: String((window as unknown as { SKILL_CATALOG?: Record<string, Row> }).SKILL_CATALOG?.[bound ? skillIdForVariable(bound.skill) : NATURAL_WEAPON_SKILL]?.item_name ?? 'Weapon Skill'),
    weaponDice: num(read.value(weapon, 'dice')), weaponDie: num(read.value(weapon, 'die')),
  };
  const command = buildWeaponAttackCommand(scope.outcome, scope.deltas, roll.draws, commandInput);
  if (scope.damageEffects) command.form_status_effects = scope.damageEffects;
  const interSwingFortunateRows = scope.outcome.interSwingFortunate ? scope.reactionEffects?.rows.filter(row =>
    row.roll_by === actorId && row.roll_item_id === FORTUNATE_ID
      && ['no_roll', 'reaction_ability_applied'].includes(row.roll_type_1)) ?? [] : null;
  if (scope.reactionEffects) {
    const { rows, ec, statusesAdded, statusesRemoved, statusStacks, charges, playerCharactersChanged } = scope.reactionEffects;
    // Preserve the bound lane's existing actor-charge wire shape, using the
    // shared frame's final values. Other holders remain in reaction transport.
    const actorCharges = input.abilityId ? charges.filter(charge => charge.holder === actorId) : [];
    if (actorCharges.length) {
      command.charges = actorCharges.map(charge => ({ ...charge, delta: charge.charges - charge.before }));
      command.frame.attacker.charges = actorCharges.map(charge => ({ item_id: charge.item_id, current: charge.charges }));
    }
    const actorChargeIds = new Set(actorCharges.map(charge => charge.item_id));
    // The existing top-level saver logs these spends. Send each actor charge
    // log through only one writer.
    const effectRows = rows.filter(row => !interSwingFortunateRows?.includes(row)
      && !(row.roll_type_1 === 'adjust_charges' && row.roll_by === actorId
        && row.roll_item_id && actorChargeIds.has(row.roll_item_id)));
    const reactionCharges = actorCharges.length ? charges.filter(charge => charge.holder !== actorId) : charges;
    // Keep the resolver's complete consequence evidence; save each identical primary scalar only once.
    const reactionEc = Object.fromEntries(Object.entries(ec).flatMap(([id, changes]) => {
      const remaining = Object.fromEntries(Object.entries(changes).filter(([key, value]) => {
        const primary: Row = id === targetId && key in command.writes.target ? command.writes.target
          : id === actorId ? command.writes.attacker : {};
        return primary[key] !== value;
      }));
      return Object.keys(remaining).length ? [[id, remaining]] : [];
    }));
    Object.assign(command, attackReactionCommand({ ...scope.reactionEffects, ec: reactionEc, rows: effectRows, charges: reactionCharges },
      encounter.encounter_creatures_arr as Row[] ?? [], (member, key) => ftEncounterCreatureField(member?.encounter_creatures, key)));
    if (scope.reactionEffects.fortunateOffers?.length) command.fortunate_offers = scope.reactionEffects.fortunateOffers;
    if (!effectRows.length && !Object.keys(reactionEc).length && !statusesAdded.length && !statusesRemoved.length && !statusStacks.length
      && !reactionCharges.length && !scope.reactionEffects.chargesRemoved?.length && !playerCharactersChanged.length && !scope.reactionEffects.spawnRows?.writes.length
      && !scope.reactionEffects.statusTriggerListeners?.length) {
      delete command.reaction_effects; delete command.reaction_expected_revs; delete command.reaction_member_before;
    }
  }
  if (approach) {
    const effects = command.reaction_effects ??= { rows: [], ec: {}, statusesAdded: [], statusesRemoved: [], statusStacks: [], charges: [], playerCharactersChanged: [] };
    // Reactions may move the actor again; their final coordinates win. CAS always
    // compares with the persisted start, never with the tentative landing.
    effects.ec[actorId] = { x_pos: approach.end.x, y_pos: approach.end.y, z_pos: approach.end.z, ...effects.ec[actorId] };
    const before = (command.reaction_member_before ??= {})[actorId] ?? {};
    (command.reaction_member_before ??= {})[actorId] = { ...before, x_pos: approach.start.x, y_pos: approach.start.y, z_pos: approach.start.z };
    (command.reaction_expected_revs ??= {})[actorId] = num(ftEncounterCreatureField(savedMembers.get(actorId)?.encounter_creatures, 'movement_rev'));
  }
  if (scope.outcome.fortunateFired || ((scope.outcome.hit || scope.outcome.rider?.hit) && shieldedChanges(target as Row).length)) {
    try {
      if (scope.outcome.fortunateFired) {
        // A paid-swing grant belongs to the shared frame. Its pending status was consumed by the rider (or remains
        // there if the paid outcome stopped the free hand). Use the native log slot without applying the S8 grant again.
        const effects: Extract<AbilityUseVerdict, { kind: 'resolved' }> = scope.outcome.interSwingFortunate ? {
          kind: 'resolved',
          rows: interSwingFortunateRows ?? [],
          ec: {}, statusesAdded: [], statusesRemoved: [], statusStacks: [], charges: [], playerCharactersChanged: [],
          inventory: [], deathTail: [], concentrationBreaks: [],
        } : statusPlan(0, true);
        delete effects.ec[actorId]; // the swing carries the same luck/reaction debit
        command.status_effects = effects;
        if (scope.outcome.interSwingFortunate) command.status_effects_before_paid = true;
      }
      if (scope.outcome.hit || scope.outcome.rider?.hit) command.damage_status_effects = statusPlan(Number(scope.outcome.hit) + Number(!!scope.outcome.rider?.hit), false);
    } catch (error) { refuseUnported(String(error instanceof Error ? error.message : error)); return 'refused'; }
  }
  const stray = scope.outcome.stray;
  if (stray) {
    const beforeReaction = num(ftEncounterCreatureField(intendedTarget.encounter_creatures, 'reaction'));
    const first = buildWeaponAttackCommand(stray.first, stray.firstDeltas, [], { ...commandInput,
      targetId: stray.intendedTargetId, targetRev: num(ftEncounterCreatureField(intendedTarget.encounter_creatures, 'movement_rev')) });
    command.stray = { target_id: stray.intendedTargetId, expected_rev: num(ftEncounterCreatureField(intendedTarget.encounter_creatures, 'movement_rev')),
      before_reaction: beforeReaction, reaction: stray.firstDeltas.target.reaction ?? beforeReaction,
      roll: first.rolls.attack!, unlucky: first.rolls.unlucky, reaction_ability_id: stray.first.reactionFired,
      double_natural_one: scope.outcome.naturalD20 === 1 };
    command.frame.stray = { target_id: command.stray.target_id,
      rev: command.stray.expected_rev + (command.stray.reaction !== beforeReaction ? 1 : 0), reaction: command.stray.reaction };
    // Reroll uses the luck left by the first d20; persist its final value against the ORIGINAL actor.
    command.writes.attacker.luck = command.frame.attacker.luck = scope.outcome.luckAfter ?? stray.first.luckAfter ?? command.before.luck;
  }
  if (ammoChange) command.inventory_change = ammoChange;
  for (const key of ['reaction_effects', 'status_effects', 'damage_status_effects', 'form_status_effects', 'paid_save_effects', 'rider_save_effects'] as const) {
    const effects = command[key];
    if (effects) effects.rows = attackStatusParticipants(effects, savedMembers).rows;
  }

  // The actor's OWN sound, picked now from the items (the same tiers the server would route: attack roll → die,
  // hit → attacker's attack pool / weapon family / damage type, miss → weapon swing / whiff, Evade → its pool,
  // kill → the victim's death), stamped on the command BEFORE the outbox freezes it and played the instant the
  // result applies — never tied to a saved roll (owner 2026-09-10). Peers play the same takes from the frame;
  // attack_truth_commit_inc.php claims the rows these cover so nothing sounds twice.
  let sound: ReturnType<typeof pickActionSound> = null;
  // Public actions only: a hidden (1) or secret (2) roll never relayed a sound to every viewer — its rows keep their
  // server route, which honours the per-viewer privacy (GM-only for hidden, silent for secret).
  if (!command.swing_skipped && (input.rollPrivate ?? 0) === 0) try {
    const taxonomy = peekAudioCatalog()?.taxonomy ?? null;
    const o = command.frame.outcome as unknown as Row;
    const cues: ActionSoundCue[] = [{ slot: 'attack', tiers: [['die_roll']] }];
    const reactionId = upper(o.reaction_fired);
    if (reactionId) cues.push({ slot: 'reaction', tiers: audioTiers(definitionFor(target as Row, encounter, reactionId) ?? { items: { item_id: reactionId, item_type: 'reaction_ability' } }, 'cast', { taxonomy }) });
    cues.push(o.hit === true
      ? { slot: 'hit', tiers: audioTiers(weapon, 'hit', { attacker, taxonomy }) }
      : { slot: 'whiff', tiers: audioTiers(weapon, 'whiff', { taxonomy }) });
    const rider = asRow(o.rider);
    if (rider && riderWeapon) {
      cues.push({ slot: 'attack:rider', tiers: [['die_roll']] });
      cues.push(rider.hit === true
        ? { slot: 'hit:rider', tiers: audioTiers(riderWeapon, 'hit', { attacker, taxonomy }) }
        : { slot: 'whiff:rider', tiers: audioTiers(riderWeapon, 'whiff', { taxonomy }) });
    }
    if (o.death === 'killed' || o.death === 'massive_damage') cues.push({ slot: 'death', tiers: audioTiers(target, 'death', { taxonomy }) });
    // The action's ONE voice line, from the rows as they stand NOW (before the result applies): the attacker's line for
    // its weapon, else the target's body line for everything that lands — both swings together, so a rider that
    // finishes the target still gives a PC's death cry. The endpoint voice-claims every row of this lane (the rows
    // arrive post-hit), so this pick is the line — or, with none, the action is voiceless.
    const catalog = peekAudioCatalog();
    const voiceCtx = catalog ? pageRollSoundContext({ encounter_id: encounterId }, catalog) : null;
    const landed = (o.hit === true ? Math.max(0, Number(o.damage) || 0) : 0) + (rider?.hit === true ? Math.max(0, Number(rider.damage) || 0) : 0);
    const voice = voiceCtx ? actionVoiceTiers(voiceCtx, { by: attacker as Row, used: weapon as Row, to: target as Row, damage: landed, seed: command.operation_id,
      // The kept natural d20 — a PC's natural 20 / 1 barks when the weapon has no line of its own (owner 2026-09-16).
      naturalD20: Number(o.naturalD20) }) : null;
    sound = pickActionSound(cues, command.operation_id, voice);
    if (sound) (command as unknown as Row).sound = sound;
  } catch (error) {
    console.warn('attack-truth: action sound pick failed (the rows keep their server route)', error);
  }
  if (!reaction?.preview && scope.reactionEffects?.fortunateOffers?.length)
    damageFortunateOffers.set(command.operation_id, scope.reactionEffects.fortunateOffers);
  return withLogParticipants(command, [...savedMembers.values()]);
}

export async function tryCommitWeaponAttack(input: WeaponAttackCommitInput): Promise<WeaponAttackCommitOutcome> {
  markStall('attack-truth:commit');
  const timing = startActionTiming('attack');
  let unsupported = false;
  try {
  observeTruthIntent('weapon', { schema: 'janus.weapon.intent.v1', input });
  if (!await awaitEntityCompileReady([input.encounterId, input.actorId, input.targetId], input.actorId)) return 'refused';
  const encounterId = upper(input.encounterId), actorId = upper(input.actorId), reservationId = crypto.randomUUID().toUpperCase();
  const windows = pageLegendaryWindowActions();
  bindActionTiming(timing, reservationId);
  const reason = windows?.claim(encounterId, actorId, reservationId)
    ?? (legendaryWindowKey(actorEncounterFor(encounterId) as Row | null, actorId) && !windows ? 'legendary-window:advance-unavailable' : null);
  if (reason) { refuseUnported(reason); return 'refused'; }
  let queued = false;
  try {
    const result = await tryCommitWeaponAttackCore(input, reservationId);
    unsupported = result === null;
    queued = result === 'handled';
    return result;
  } finally { if (!queued) windows?.rejected(reservationId); }
  } finally { endActionTiming(timing, unsupported); }
}
/** Reactions and derived status projections need authored status and environment definitions before the draw or recovery.
 * A catalog display identity cannot satisfy this warm-up, even when projection can use it for a source label. */
function hydrateRosterStatusDefinitions(encounterId: string, encounter = actorEncounterFor(encounterId) as Row | null): Promise<void> | null {
  const roster = Array.isArray(encounter?.encounter_creatures_arr) ? encounter!.encounter_creatures_arr as unknown[] : [];
  const missing = new Set<string>();
  for (const raw of roster) {
    const row = asRow(raw);
    if (!row) continue;
    for (const id of possessorEnvironmentDefinitionIds(row)) if (!definitionFor(row, encounter!, id, 'any')) missing.add(id);
    const read = attackTriggerReaders(encounter!, row);
    const statuses = ftCreatureStatuses(row, 'target') as Row[];
    for (const id of contingencySpellIds(statuses)) if (!read.definition(id, true)) missing.add(id);
    for (const status of statuses) {
      const id = upper(scalar(status.status_item_id) ?? ftItemsCol(asRow(status.status_arr), 'item_id'));
      if (/^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(id) && !read.definition(id, true)) missing.add(id);
    }
  }
  return missing.size ? Promise.all([...missing].map(id => fetchAndCacheObject(id).catch(() => undefined))).then(() => undefined) : null;
}
/** Consent/resource suspension resumes the same raw revision as the first attempt. */
function frozenAttackFrame(encounter: Row, attacker: MovementTruthRow, target: MovementTruthRow) {
  const actorId = upper(ftEncounterCreatureField(attacker.encounter_creatures, 'encounter_creature_id'));
  const targetId = upper(ftEncounterCreatureField(target.encounter_creatures, 'encounter_creature_id'));
  const frame = frameSnapshot(encounter, new Map([[actorId, attacker], [targetId, target]]))();
  return { encounter: frame.encounter, attacker: frame.members.get(actorId) as MovementTruthRow,
    target: frame.members.get(targetId) as MovementTruthRow };
}

function hydrateMovementDeathResources(encounterId: string): Promise<void> | null {
  const statuses = hydrateRosterStatusDefinitions(encounterId);
  const encounter = actorEncounterFor(encounterId) as Row | null;
  if (encounter && !definitionFor(null, encounter, OPPORTUNITY_ATTACK_ID, true))
    return Promise.all([statuses, fetchAndCacheObject(OPPORTUNITY_ATTACK_ID)]).then(() => undefined);
  return statuses;
}
/** Creature and weapon passives share the same referenced-status warm-up. */
function hydrateAttackPassiveDefinitions(actor: Row, encounter: Row, weapon: Row | null, partners: readonly Row[] = []): Promise<string | null> | null {
  const read = explicitWeaponReaders(actor, encounter);
  const entries = weapon ? [weapon, ...partners].flatMap(source => explicitWeaponPassiveEntries(source, actor, read)) : ftLayerEntries(actor, 'passive_ability');
  const references = entries.filter(entry => !asRow(entry) || passiveActive(read, asRow(entry)!)).map(entry => {
    const def = carrierDefinition(actor, encounter, entry);
    if (def && ftLayerEntries(def, 'ability_charges').length) {
      const pool = entryCharges(entry, upper(ftItemsCol(def, 'item_id')), { value: ftLayerValue, resolveItem: () => def });
      if (pool?.currentHeld && pool.current !== null && pool.current !== '' && Number(pool.current) <= 0) return null;
    }
    return def;
  }).filter((def): def is Row => !!def)
    .map(passive => ({ passive, keys: Object.keys(asRow(passive.item_layer_types) ?? {}).filter(key => key.startsWith('status_effect_applied_to_')) }))
    .filter(({ keys }) => keys.length > 0);
  if (!references.length) return null;
  const missing = (): string | null => {
    for (const { passive, keys } of references) {
      for (const key of keys) {
        for (const entry of ftLayerEntries(passive, key)) {
          const id = upper(readLayerListEntryUuid(entry));
          if (id && !read.resolveItem?.(id))
            return 'weapon:family:weapon-passive-definition-unavailable:' + String(ftItemsCol(passive, 'item_name') ?? ftItemsCol(passive, 'item_id'));
        }
      }
    }
    return null;
  };
  if (!missing()) return null;
  // One missing-id batch covers creature, selected weapon and other-hand grants.
  // Failed/missing responses retain their named pre-draw refusal; warm reactions stay synchronous.
  return hydrateReferencedDefinitions(actor, encounter, references.map(({ passive }) => passive)).then(missing, missing);
}
/** Join only definitions this attack can read. Inventory binding and deferred
 * death packages retain their own waits; turn/orb and rubble preloads are unrelated. */
async function hydrateAttackInputs(input: WeaponAttackCommitInput): Promise<string | null> {
  const encounter = actorEncounterFor(upper(input.encounterId)) as Row | null;
  if (!encounter) return null;
  await hydrateRosterStatusDefinitions(upper(input.encounterId), encounter);
  const actor = actorRowFor(upper(input.encounterId), upper(input.actorId)) as Row | null;
  const target = actorRowFor(upper(input.encounterId), upper(input.targetId)) as Row | null;
  await hydrateStatusCreatureDefinitions(Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : [],
    id => definitionFor(actor, encounter, id, 'any'), fetchAndCacheObject);
  const ids = new Set([FORTUNATE_ID,
    ...(target && naturalWeaponIdsOf(target).includes(EVADE_ID) ? [EVADE_ID] : []),
    ...(actor && !inventoryWeaponSelected(actor, input.weaponId) ? [upper(input.weaponId)] : []),
    ...(input.abilityId ? [upper(input.abilityId)] : [])]);
  await Promise.all([...ids].filter(id => !definitionFor(actor, encounter, id, 'any'))
    .map(id => fetchAndCacheObject(id).catch(() => undefined)));
  const inventory = actor ? findInventoryRow(actor, upper(input.weaponId)) : null;
  const templateId = inventory ? upper(ftInventoryField(inventory, 'inventory_item_id')) : '';
  if (actor) {
    const weapon = inventory ?? definitionFor(actor, encounter, upper(input.weaponId));
    const read = explicitWeaponReaders(actor, encounter), weaponId = upper(input.weaponId);
    const paidHand = weaponId === upper(readLayerListEntryUuid(read.value(actor, 'equipped_main_hand_natural_weapon_item_id'))) ? 'main'
      : weaponId === upper(readLayerListEntryUuid(read.value(actor, 'equipped_off_hand_natural_weapon_item_id'))) ? 'off' : undefined;
    const ability = input.abilityId ? definitionFor(actor, encounter, upper(input.abilityId)) : null;
    const source = weapon && target && !input.opportunityAttack && input.attackMode !== 'throw_weapon'
      && !ftLayerEntries(ability, 'attack_ability_throws_the_throwing_weapon_at_target').length
      && !num(ftEncounterCreatureField(target.encounter_creatures, 'encounter_creature_dead'))
      ? weaponAttackPartnerSource(weapon, actor, read, paidHand) : null;
    const failure = await hydrateAttackPassiveDefinitions(actor, encounter, weapon, source && !('reason' in source) ? [source.partner] : []);
    if (failure) return failure;
  }
  const sources = [...ids, ...(templateId ? [templateId] : [])].map(id => definitionFor(actor, encounter, id, 'any'));
  if (inventory) sources.push(inventory);
  // The selected attack and any held listener can apply a status not yet present
  // on the roster. Join their reference closure before the synchronous swing.
  if (actor) await hydrateTurnTriggerDefinitions(actor, encounter, sources.filter((source): source is Row => !!source),
    Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : []);
  if (!actor || !sources.some(source => source && ['push_ft_after_hit', 'pull_ft_after_hit', 'pull_to_caster_center']
    .some(key => ftLayerValue(source, key) != null))) return null;
  // A hit can move its victim over a ledge after its damage dice. Hold the same
  // fall package as ordinary movement, including its authored damage references.
  const falls = await Promise.all(FALL_DEFINITIONS.map(id => definitionFor(actor, encounter, id, 'any')
    ?? fetchAndCacheObject(id).catch(() => null)));
  await hydrateReferencedDefinitions(actor, encounter, falls.filter((def): def is Row => def !== null));
  return null;
}
registerMovementStatusHydrator(hydrateMovementDeathResources);
async function tryCommitWeaponAttackCore(input: WeaponAttackCommitInput, reservationId: string): Promise<WeaponAttackCommitOutcome> {
  if (!await awaitEntityCompileReady([input.encounterId, input.actorId, input.targetId], input.actorId)) return 'refused';
  // Attached natural attacks also read inventory in the shared weapon selector.
  if (input.abilityId && !await hydrateColdWeaponBinding(input.actorId, input.weaponId,
      () => actorRowFor(upper(input.encounterId), upper(input.actorId)) as Row | null, true)) {
    reportWeaponAttackRefusal('weapon:inventory-instance-unavailable', { abilityId: input.abilityId, weaponId: input.weaponId }); return 'refused';
  }
  // A painted actor may have a newer inventory facet still adopting. Join it without fetching definitions.
  if (!await hydrateColdWeaponBinding(input.actorId, input.weaponId, () => actorRowFor(upper(input.encounterId), upper(input.actorId)) as Row | null)) {
    reportWeaponAttackRefusal('weapon:inventory-instance-unavailable', { abilityId: input.abilityId, weaponId: input.weaponId }); return 'refused';
  }
  markActionTiming(reservationId, 'decide_ms');
  const reactionEncounter = actorEncounterFor(upper(input.encounterId)) as Row | null;
  const reactionAttacker = actorRowFor(upper(input.encounterId), upper(input.actorId)) as Row | null;
  const inputFailure = await withAsyncDeadline(() => hydrateAttackInputs(input), 20_000)
    .catch((error: unknown) => error instanceof DOMException && error.name === 'TimeoutError'
      ? 'weapon:attack:input-wait-timeout' : 'weapon:attack:input-definitions-unavailable');
  if (inputFailure) { reportWeaponAttackRefusal(inputFailure, { abilityId: input.abilityId, weaponId: input.weaponId }); return 'refused'; }
  // FT3D object picks are inventory identities, never creature roster entries.
  // Keep the exact weapon binding, then use the ordinary object verdict/save lane.
  if (reactionEncounter && reactionAttacker && !actorRowFor(upper(input.encounterId), upper(input.targetId))
      && findInventoryRow(reactionEncounter, upper(input.targetId))) {
    const fail = (reason: string): 'refused' => { refuseUnported(reason, { weaponId: input.weaponId }); return 'refused'; };
    if (upper(ftEncounterCreatureField(reactionAttacker.encounter_creatures, 'encounter_creature_created_by_id')) !== upper(window.FateTide?.user?.id)
        && window.FateTide?.user?.isGM !== true) return fail('weapon:actor-not-authorized');
    const read = explicitWeaponReaders(reactionAttacker, reactionEncounter);
    const ability = input.abilityId ? definitionFor(reactionAttacker, reactionEncounter, input.abilityId) : plainWeaponAbility(input.weaponId);
    if (!ability) return fail('weapon:attack-definition-unavailable');
    const throwing = input.attackMode === 'throw_weapon' || ftLayerEntries(ability, 'attack_ability_throws_the_throwing_weapon_at_target').length > 0;
    const inventory = inventoryWeaponSelected(reactionAttacker, input.weaponId);
    const binding = inventory ? bindExplicitWeaponAttack(ability, reactionAttacker, input.weaponId, read, throwing)
      : { weapon: definitionFor(reactionAttacker, reactionEncounter, input.weaponId), ability };
    if ('reason' in binding) return fail(binding.reason);
    if (!binding.weapon) return fail('weapon:weapon-definition-unavailable');
    const family = 'skill' in binding ? explicitWeaponFamilyRefusal(binding, reactionAttacker, read, throwing) : null;
    if (family) return fail(family);
    const templateId = upper(ftInventoryField(binding.weapon, 'inventory_item_id'));
    const template = templateId ? definitionFor(reactionAttacker, reactionEncounter, templateId) : binding.weapon;
    if (!template) return fail('weapon:weapon-definition-unavailable');
    const weapon = objectWeaponAttackDefinition(template, binding.weapon, throwing, read);
    return tryCommitWeaponObjectAttack(input, weapon, reservationId);
  }
  if (reactionEncounter) {
    try {
      const read = abilityTruthReaders(reactionAttacker ?? {}, reactionEncounter, new Set([upper(input.actorId), upper(input.targetId)]));
      input = { ...input, deathResources: lazyDeathResources(
        reactionEncounter.encounter_creatures_arr as Row[] ?? [], reactionEncounter, read, campaignFor(reactionEncounter)) };
    } catch (error) { refuseUnported(String(error instanceof Error ? error.message : error)); return 'refused'; }
  }
  const windowReason = pageLegendaryWindowActions()?.validate(reservationId);
  if (windowReason) { refuseUnported(windowReason); return 'refused'; }
  let needsConsent = false, needsAbilityLane = false;
  const rolls = resumableRollSource(browserRollSource());
  let frozen: { encounter: Row; attacker: MovementTruthRow; target: MovementTruthRow; frozen: true } | undefined;
  const freeze = (): void => {
    if (frozen) return;
    const encounter = actorEncounterFor(upper(input.encounterId)), attacker = actorRowFor(upper(input.encounterId), upper(input.actorId)), target = actorRowFor(upper(input.encounterId), upper(input.targetId));
    if (encounter && attacker && target) frozen = { ...frozenAttackFrame(encounter, attacker, target), frozen: true };
  };
  const command = await planWithConsent(upper(input.encounterId), upper(input.actorId), consent => resumeDeathResources(() => {
    needsConsent = false;
    const windowReason = pageLegendaryWindowActions()?.validate(reservationId);
    if (windowReason) { refuseUnported(windowReason); return 'refused'; }
    const probe: NonNullable<WeaponAttackCommitInput['consentProbe']> = { evade: false };
    const encounterId = upper(input.encounterId), answers: Record<string, boolean> = {};
    for (const row of (actorEncounterFor(encounterId)?.encounter_creatures_arr as Row[] ?? [])) {
      const id = upper(ftEncounterCreatureField(row.encounter_creatures, 'encounter_creature_id'));
      const answer = consent.answer(id, EVADE_ID);
      if (answer !== undefined || consent.final) answers[id] = answer === true;
    }
    const planned = prepareWeaponAttackCommand({ ...input, rollSource: rolls.next(), triggerConsent: consent, consentProbe: probe, evadeAnswers: answers }, frozen);
    needsAbilityLane = planned === null && probe.naturalWeapon !== undefined;
    if (planned === null && probe.evade) for (const targetId of probe.targets ?? [upper(input.targetId)]) {
      const target = actorRowFor(encounterId, targetId) as Row | null;
      const controllerUserId = target ? triggerController(encounterId, target) : null;
      if (controllerUserId) consent.need({ key: consentKey(targetId, EVADE_ID), reactorId: targetId, abilityId: EVADE_ID, abilityName: 'Evade',
        trigger: 'triggers_on_roll_type_against_the_caster', controllerUserId });
    }
    needsConsent = consent.pending().length > 0;
    if (needsConsent && rolls.draws.length && !frozen) {
      freeze();
    }
    return planned;
  }, freeze), planned => planned === null && needsConsent);
  if (command === null && needsAbilityLane && !rolls.draws.length) return null;
  if (command === null) { refuseUnported('weapon:trigger-consent-unresolved', { weaponId: input.weaponId }); return 'refused'; }
  if (command === 'refused') return command;
  const encounterId = command.encounter_id, actorId = command.actor_id;
  const attacker = actorRowFor(encounterId, actorId), target = actorRowFor(encounterId, command.target_id);
  if (!attacker || !target) return 'refused';
  const sound = (command as unknown as { sound?: ReturnType<typeof pickActionSound> }).sound;
  // OWNERSHIP BOUNDARY (Codex S4 review F1): from the enqueue attempt on, the click belongs to this lane. Nothing
  // below may reach the caller as a rejection — a throw after a successful enqueue (or inside the enqueue-error
  // recovery) would let use_ability_inc.php fire an attack that is already applied or queued. Every path
  // returns 'handled' or 'refused'.
  const base = command as unknown as ActorTruthCommandBase;
  bindActionTiming(reservationId, command.operation_id);
  const outbox = actorOutbox(encounterId, actorId);
  let enqueued = false;
  let completion: Promise<unknown>;
  try {
    pageLegendaryWindowActions()?.bind(reservationId, command.operation_id);
    stageAppliedEntities(base.operation_id, attackEffects(command).map(effect => effect.spawnRows));
    completion = outbox.enqueue(withLogParticipants(base)).commit;
    enqueued = true;
  } catch (error) {
    try { forgetRejectedAttack(base); render(base, attacker, target); } catch (recoveryError) { console.error('attack-truth: revert after a failed enqueue threw', recoveryError); }
    try { if (typeof displayWarning === 'function') displayWarning('Attack could not be committed: ' + (error instanceof Error ? error.message : String(error))); } catch { /* presentation only */ }
    try { window.dispatchEvent(new CustomEvent('ft:attack-truth-refused', { detail: { command, info: { error: String(error), status: 0, terminal: true } } })); } catch { /* observers only */ }
    return 'refused';
  }
  try {
    noteOwnOperation(command.operation_id, WEAPON_ATTACK_SCHEMA);
    seen.add(command.operation_id);
    // Cast-sync #8c (owner: start the swing at send): the swing starts now, its strike and the weapon's hit/whiff
    // take land together on the impact clock; the dice clatter stays at send.
    const impactAt = presentWeaponAttack(command);
    if (sound) void afterActionPaint(() => playActionSound(command.operation_id, sound, { hitAt: impactAt }));
  } catch (error) {
    console.error('attack-truth: bookkeeping after enqueue threw (the operation is queued and stays owned here)', error);
  }
  if (ftCurrentTruthSink() && !isMovementTruthAcceptedResult(await completion!)) return 'refused';
  return enqueued ? 'handled' : 'refused';
}

type AttackTruthWireFrame = ActorTruthCommandBase & { swing_skipped?: true; target_id: string; rev: number; target_rev: number; frame: AttackTruthFrame; provisional?: boolean; committed?: boolean; correction?: boolean; authoritative?: Row };
/** Page-pushed truth banks each participant against its own confirmed revision; a refused save resyncs the encounter. */
export function handleAttackTruth(command: AttackTruthWireFrame): void {
  if (command?.schema !== WEAPON_ATTACK_SCHEMA || !Number.isInteger(command.rev) || command.rev < 0) return;
  if (rejectRetiredAttack(command)) return;
  const kind = command.correction ? 'correction' : 'committed';
  const targetId = upper(command.target_id);
  const attacker = actorRowFor(command.encounter_id, command.actor_id);
  const target = actorRowFor(command.encounter_id, targetId);
  const frame = asRow(command.frame) as unknown as AttackTruthFrame | null;
  if (!attacker || !frame || !asRow(frame.attacker) || !Number.isInteger(frame.attacker.aps)) return;
  acknowledgeFortunate(command, attacker as Row, frame);
  if (!noteFrameSeen(command.operation_id, kind)) return;
  // A peer hears the actor's picked takes on the FIRST frame of the operation it sees (once per operation).
  if (kind !== 'correction' && !command.swing_skipped && Number((command as unknown as Row).roll_private ?? 0) === 0) {
    // A public peer frame presents its gesture even without sound; relayed sound shares the impact clock (#8c).
    const relayed = (command as unknown as Row).sound;
    const impactAt = presentWeaponAttack(commandOf(command));
    void playActionSound(command.operation_id, relayed, { peer: true, ...(impactAt !== undefined ? { hitAt: impactAt } : {}) });
  }
  const banked = confirmedRevFor(command.encounter_id, command.actor_id, attacker);
  bankConfirmedRev(command.encounter_id, command.actor_id, attacker, command.rev, { aps: Number(frame.attacker.aps) });
  if ((command as unknown as Row).applied_frame && kind !== 'correction') {
    // Inventory receipts settle after local apply, outside its computed delta.
    if (!seen.has(command.operation_id) && command.rev >= banked) {
      projectInventorySnapshot(command, frame.inventory_state, frame.inventory_state_rev ?? command.rev);
      if ((command as unknown as Row).replay !== true && (frame as unknown as Row).replayed !== true) projectThrownWeapon(command);
    }
    pending.delete(command.operation_id); render(command, attacker, target); return;
  }
  const targetLanded = kind === 'committed' && !seen.has(command.operation_id)
    ? landTargetTruth(command.encounter_id, targetId, target, frame, Number.isInteger(command.target_rev) ? command.target_rev : undefined)
    : false;
  pending.delete(command.operation_id);
  if (kind === 'committed' && seen.has(command.operation_id)) {
    // Our own: accepted() promotes it — but the target side may still be owed when the frame outran the answer.
    landTargetTruth(command.encounter_id, targetId, target, frame, Number.isInteger(command.target_rev) ? command.target_rev : undefined);
    landReactionTruth(command.encounter_id, frame.reaction_members);
    return;
  }
  if (kind === 'correction') {
    fortunatePending.correct(fortunateActorKey(command.encounter_id, command.actor_id), command.operation_id);
    if (!applyRawAttackCorrection(command, asRow(command.authoritative))) reloadRefusedEncounter(command.encounter_id, command.operation_id);
    return;
  } else if (command.rev <= banked || command.rev < Number(attacker.encounter_creatures.movement_rev ?? 0)) {
    // Older than what this browser already rendered for the ATTACKER (or behind its own optimistic work): the
    // attacker side stays; the target side was ordered on its own revision above.
  } else {
    if (frame.outcome?.fortunate_pending === true) fortunatePending.begin(fortunateActorKey(command.encounter_id, command.actor_id), command.operation_id);
    projectStatusSnapshot(command.encounter_id, attacker as Row, frame.attacker.statuses);
    applyAttackTruth(attacker as Row, null, frame);
    projectInventorySnapshot(command, frame.inventory_state, frame.inventory_state_rev ?? command.rev);
    projectStrayTruth(command, frame.stray, true);
    if ((command as unknown as Row).replay !== true && (frame as unknown as Row).replayed !== true) projectThrownWeapon(command);
  }
  if (pendingFor(command.encounter_id, command.actor_id) === 0) {
    (attacker.encounter_creatures as Row).aps = confirmedApsFor(command.encounter_id, command.actor_id, attacker);
    reconcileActorRow(command.encounter_id, command.actor_id, attacker);
  }
  render(command, attacker, targetLanded ? target : null);
  if (kind === 'committed') landReactionTruth(command.encounter_id, frame.reaction_members);
}

// ---- fetch-on-ADD interning (owner Q2) ------------------------------------------------------------
// The S1 census: the ONLY attack input the browser does not already hold is the weapon DEFINITION (a
// natural weapon is a catalog row the attacker's inventory never carries). Intern every natural-weapon
// definition of a row when it is INSTALLED — the encounter snapshot, a caster_update, a delta — never
// during a resolve. One GET per definition per session, cached in objectCache.
/** Definitions known to sit in the SHARED objectCache (a row- or encounter-local catalog hit never sets this —
 * another creature in another encounter would then skip a fetch it needs; Codex S4 review F3). A pending fetch
 * is only ever "pending", and a failed one leaves no marker, so it is retried on the next install (F2). */
const interned = new Set<string>();
const inflight = new Set<string>();
/** Lane A6 for N4b (ask 4, 2026-10-01) — aligned with websocket/object-cache.ts's held rule: the shared cache holds the REQUESTED catalog
 *  definition itself (items.item_id = the id), WITH layers or as a layerless template ({items, computed} — Awake, Fully Rested, Performing a
 *  Melody). The old "non-empty item_layer_types" test never passed for those, so every install re-asked for them (playtest finding #4). An
 *  identity shell (items only, no layers, no computed) is still not held. A LAYERLESS hit is held NOW but never interned for the session
 *  (Codex A6 review 2 P2): object-cache.ts drops it when the definition epoch moves, so the next install sees the miss and re-fetches. */
const inSharedCache = (id: string): 'layers' | 'layerless' | false => {
  const full = asRow(window.FateTide?.objectCache?.get(id));
  const items = asRow(full?.items);
  if (!full || !items || upper(items.item_id) !== upper(id)) return false;
  if (hasDefinitionLayers(full)) return 'layers';
  return asRow(full.item_layer_types) || asRow(full.computed) ? 'layerless' : false;
};
/** The definitions a resolve needs from this row: its natural weapons AND its reaction abilities — the resolver reads
 *  Evade's own condition layers (S6, 2026-09-08), so Evade is interned on ADD exactly like the weapons. */
function naturalWeaponIdsOf(row: Row): string[] {
  const creature = asRow(asRow(row.computed)?.creature);
  // S9: the hands' natural ids from the compiled equipped cluster too — the two-weapon rider resolves the OTHER hand by
  // id (duel_weapon_attack_roll.php), so its definition must be interned on install exactly like the clicked weapon's.
  const equipped = asRow(creature?.equipped);
  const lists = [asRow(creature?.abilities)?.natural_weapon, creature?.natural_weapon, asRow(row.item_layer_types)?.natural_weapon,
    equipped?.equipped_main_hand_natural_weapon_item_id, equipped?.equipped_off_hand_natural_weapon_item_id];
  const reactions = [asRow(creature?.abilities)?.reaction_ability, asRow(row.item_layer_types)?.reaction_ability];
  const idOf = (entry: unknown): string => upper(typeof entry === 'string' ? entry : (asRow(entry)?.ability_id ?? asRow(asRow(entry)?.items)?.item_id ?? asRow(entry)?.item_id));
  const ids: string[] = [];
  for (const list of lists) {
    for (const entry of Array.isArray(list) ? list : (list ? [list] : [])) {
      const id = idOf(entry);
      if (/^[0-9A-F]{8}-/.test(id) && !ids.includes(id)) ids.push(id);
    }
  }
  // Only EVADE among the reactions (the one reaction definition the resolver reads) — never every reaction on the row.
  for (const list of reactions) {
    for (const entry of Array.isArray(list) ? list : (list ? [list] : [])) if (idOf(entry) === EVADE_ID && !ids.includes(EVADE_ID)) ids.push(EVADE_ID);
  }
  return ids;
}
export function internAttackDefinitions(row: Row, encounter: Row | null): void {
  const statuses = ftCreatureStatuses(row, 'target') as Row[];
  if (statuses.some(isFortunateStatus)) {
    const ec = asRow(row.encounter_creatures);
    fortunatePending.waiting(fortunateActorKey(String(ec?.encounter_id ?? ''), String(ec?.encounter_creature_id ?? '')), true);
  }
  const ids = naturalWeaponIdsOf(row);
  const naturalBase = naturalMitigationBaseId(row);
  if (num(ftLayerValue(row, 'damage_mitigation')) > 0 && naturalBase) ids.push(naturalBase);
  const opportunity = asRow(asRow(asRow(row.computed)?.creature)?.opportunity_attack);
  if (opportunity?.supported === true) {
    const weaponId = upper(opportunity.weapon_id);
    // Equipped weapons are held inventory instances. Only a natural weapon id
    // belongs in this definition warm-up; every install must keep the instance.
    for (const id of [heldInventoryWeaponSelected(row, weaponId) ? '' : weaponId, OPPORTUNITY_ATTACK_ID, EVADE_ID])
      if (id && !ids.includes(id)) ids.push(id);
  }
  if (ids.length && !ids.includes(FORTUNATE_ID)) ids.push(FORTUNATE_ID);
  let maimedActive = false;
  try { maimedActive = !!encounter && houseRuleActive(campaignFor(encounter), CRIT_CASCADE_RULES.maimed.id); } catch { /* campaign not installed yet */ }
  if (maimedActive || ids.includes(FORTUNATE_ID)) {
    const fortitude = asRow(asRow(asRow(asRow(row.computed)?.creature)?.skills)?.fortitude);
    const lines = asRow(fortitude?.adv_dis_breakdown)?.lines;
    if (Array.isArray(lines)) for (const raw of lines) {
      const line = asRow(raw);
      // Compiler-marked status hrefs identify instance rows even after those statuses leave the held bucket.
      if (!line || line.kind === 'status_effect' || 'global_layer' in line) continue;
      const id = upper(line.href_id);
      const statusSource = statuses.some(status => upper(statusScalar(status, 'status_id')) === id);
      if (id && !statusSource && !catalogShell(row,id) && !catalogShell(encounter,id) && !ids.includes(id)) ids.push(id);
    }
    for (const entry of carrierEntries(row)) {
      const e = asRow(entry);
      const nested = e?.item_arr ?? e?.ability_arr ?? entry;
      const id = upper(typeof nested === 'string' ? nested : e?.ability_id ?? e?.item_id ?? ftItemsCol(asRow(nested), 'item_id'));
      if (id && !ids.includes(id)) ids.push(id);
    }
    for (const status of statuses) {
      const id = upper(statusScalar(status, 'status_item_id') ?? ftItemsCol(asRow(status.status_arr), 'item_id'));
      if (id && !ids.includes(id)) ids.push(id);
    }
  }
  for (const id of ids) {
    if (interned.has(id) || inflight.has(id)) continue;
    const held = inSharedCache(id);
    if (held === 'layers') { interned.add(id); continue; }
    if (held === 'layerless') continue; // held now; re-checked on the next install (object-cache.ts invalidates it on a definition save)
    if (definitionFor(row, encounter, id, true)) continue; // a complete layerless status is held too; no session-wide claim
    inflight.add(id);
    void fetchAndCacheObject(id)
      .then(() => { if (inSharedCache(id) === 'layers') interned.add(id); })
      .catch(() => undefined)
      .finally(() => inflight.delete(id));
  }
}
function internEncounter(encounter: Row | null): void {
  const roster = encounter?.encounter_creatures_arr;
  if (!Array.isArray(roster)) return;
  for (const row of roster) { const r = asRow(row); if (r) internAttackDefinitions(r, encounter); }
}
casterRowInstalled.push((_casterId, row, _local) => {
  const encounterId = upper(asRow((row as unknown as Row).encounter_creatures)?.encounter_id);
  if (!_local) internAttackDefinitions(row as unknown as Row, encounterId ? actorEncounterFor(encounterId) : null);
});
window.addEventListener('ft:encounter-state-received', event => {
  const detail = (event as CustomEvent).detail;
  // The one-row echo interns only its patched rows (ft:encounter-rows-patched below), not the roster.
  if (detail?.subType === 'creature_rows_patched') return;
  internEncounter(asRow(detail?.encounterArr));
});
window.addEventListener('ft:encounter-installed', event => {
  internEncounter(asRow((event as CustomEvent).detail?.encounterArr));
});
// The one-row echo (encounter-update.ts handleEncounterRowsPatched, 2026-09-10): only the rows the delta
// patched are re-interned — never the whole roster for a two-creature position delta.
window.addEventListener('ft:encounter-rows-patched', event => {
  const detail = (event as CustomEvent).detail;
  const encounter = asRow(detail?.encounterArr);
  for (const row of Array.isArray(detail?.rows) ? detail.rows : []) { const r = asRow(row); if (r) internAttackDefinitions(r, encounter); }
});
// Rows already cached when this bundle loads (the page's initial snapshot precedes the websocket bundle).
try { for (const encounter of window.FateTide?.encounterCache?.values() ?? []) internEncounter(asRow(encounter)); } catch { /* cache not ready */ }

/** READ-ONLY dry run for harnesses (A2 S9, 2026-09-09): resolves the attack against the rows the browser holds with a roll
 *  source that THROWS on the first draw — so the answer is either the pre-draw handback reason, the local refusal text, or
 *  `in_scope: true` (the boundary was crossed). Nothing is drawn, applied, queued or posted. Optional `attackerOverrides` /
 *  `targetOverrides` patch a COPY of the encounter_creatures scalars (e.g. luck / reaction / aps) so a fixture state can be
 *  probed before it is written. `fortunateOnGain` exposes the universal-Fortunate gate alone. */
class DryRunDraw extends Error { constructor() { super('__dry_run_draw__'); this.name = 'DryRunDraw'; } }
function dryRunWeaponAttack(input: WeaponAttackCommitInput & { attackerOverrides?: Row; targetOverrides?: Row }): Row {
  const encounterId = upper(input.encounterId), actorId = upper(input.actorId), weaponId = upper(input.weaponId);
  let targetId = upper(input.targetId);
  const encounter = actorEncounterFor(encounterId), attackerRow = actorRowFor(encounterId, actorId), targetRow = actorRowFor(encounterId, targetId);
  if (!encounter || !attackerRow || !targetRow) return { in_scope: false, reason: 'rows-unavailable' };
  const patch = (row: Row, overrides?: Row): Row => overrides ? { ...row, encounter_creatures: { ...(asRow(row.encounter_creatures) ?? {}), ...overrides } } : row;
  const attacker = patch(attackerRow as Row, input.attackerOverrides), target = patch(targetRow as Row, input.targetOverrides);
  const inventory = findInventoryRow(attacker, weaponId);
  const ability = input.abilityId ? definitionFor(attacker, encounter, upper(input.abilityId)) ?? {} : plainWeaponAbility(weaponId);
  const throwingWeapon = input.attackMode === 'throw_weapon' || ftLayerEntries(ability, 'attack_ability_throws_the_throwing_weapon_at_target').length > 0;
  if (!inventory && throwingWeapon)
    return { in_scope: false, reason: 'weapon:natural-cannot-be-thrown' };
  const bindingRead = explicitWeaponReaders(attacker, encounter);
  const binding = inventory ? bindExplicitWeaponAttack(ability, attacker, weaponId, bindingRead, throwingWeapon)
    : input.abilityId ? bindNaturalWeaponAttack(ability, attacker, weaponId, bindingRead) : null;
  if (binding && 'reason' in binding) return { in_scope: false, reason: binding.reason };
  const bound = binding && !('reason' in binding) ? { ...binding, inventoryId: weaponId } : undefined;
  const weapon = bound?.weapon ?? definitionFor(attacker, encounter, weaponId);
  if (!weapon) return { in_scope: false, reason: 'weapon-definition-missing' };
  const throwing = { d: (): number => { throw new DryRunDraw(); }, fraction: (): number => { throw new DryRunDraw(); } };
  try {
    const read = weaponPartnerReaders(readers(attacker, encounter, bound), attacker, encounter, undefined, throwingWeapon);
    const scope = resolveWeaponAttackWithReactions({ encounterId, actorId, targetId, weaponId, explicitInventoryWeapon: !!bound && !!inventory,
      opportunityAttack: input.opportunityAttack, throwWeapon: throwingWeapon, rawDiceMod: input.rawDiceMod ?? 0, rawDiceAdvDisStacks: input.rawDiceAdvDisStacks ?? 0, rollPrivate: input.rollPrivate ?? 0 },
      encounter, attacker, target, read, abilityTruthReaders(attacker, encounter, new Set([actorId, targetId])), throwing as never,
      undefined, undefined, bound?.ability);
    if (!scope.supported) return scope.refused ? { in_scope: false, refused: true, message: scope.message } : { in_scope: false, reason: scope.reason };
    return { in_scope: true, note: 'resolved without a draw (no dice needed)' };
  } catch (error) {
    if (error instanceof DryRunDraw) return { in_scope: true };
    return { in_scope: false, reason: String(error instanceof Error ? error.message : error), threw: true };
  }
}
function probeFortunateOnGain(encounterId: string, actorId: string, overrides?: Row): Row {
  const encounter = actorEncounterFor(upper(encounterId)), row = actorRowFor(upper(encounterId), upper(actorId));
  if (!encounter || !row) return { available: false, reason: 'rows-unavailable' };
  const patched = overrides ? { ...(row as Row), encounter_creatures: { ...(asRow((row as Row).encounter_creatures) ?? {}), ...overrides } } : row as Row;
  try { return { available: true, gate: fortunateOnGain(patched, encounter) }; }
  catch (error) { return { available: false, reason: String(error instanceof Error ? error.message : error) }; }
}
Object.assign(window, { ftTryCommitWeaponAttack: tryCommitWeaponAttack, ftInternAttackDefinitions: internEncounter,
  ftAttackTruthProbe: { dryRun: dryRunWeaponAttack, fortunateOnGain: probeFortunateOnGain } });
export { EVADE_ID, NATURAL_WEAPON_SKILL };
